<template>
  <div class="bulk-upload">
    <div class="d-flex align-center pt-5 pb-3">
      <v-btn @click="goToProducts()" class="ml-3" icon><v-icon color="black">mdi-arrow-left</v-icon></v-btn>
      <div class="page-title mono pl-3">{{$t('bulk_upload_title')}}</div>
      <v-spacer></v-spacer>
    </div>
    <div class="bulk-row">
      <v-stepper class="bulk-upload-ctn" :value="step">
        <v-stepper-header>
          <v-stepper-step step="1">{{ $t('bulk_upload_step_1') }} </v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step step="2">{{ $t('bulk_upload_step_2') }}</v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step step="3">{{ $t('bulk_upload_step_3') }}</v-stepper-step>
        </v-stepper-header>

        <v-stepper-items>
          <v-stepper-content step="1">
            <div class="row ma-2">
              <v-col class="infos font-weight-medium">
                <div class="title">{{ $t('bulk_upload_select_category') }}</div>
                <div class="hint">{{ $t('bulk_upload_select_category_hint') }}</div>
                <v-radio-group v-model="settings.selectedTemplate" row>
                  <v-radio v-for="template in templates" :key="template.key" :label="$t(`bulk_upload_${template.key}`)" :value="template"></v-radio>
                  <v-radio class="py-2" :label="$t('bulk_upload_template_copy')" value="copy"></v-radio>
                </v-radio-group>
                <v-autocomplete
                  v-if="settings.selectedTemplate === 'copy'"
                  solo
                  clearable
                  :placeholder="$t('bulk_upload_template_search_product')"
                  v-model="settings.templateProduct"
                  :items="filteredProducts"
                  :loading="isLoadingProduct"
                  no-filter
                  :search-input.sync="searchProduct"
                  return-object
                  item-value="id"
                  item-text="name"
                  @blur="onSearchProductBlur"
                >
                  <template v-slot:selection="{ item }">
                    <span>{{ item.name }} - {{ item.sku }}</span>
                  </template>
                  <template v-slot:item="{ item }">
                    <span>{{ item.name }} - {{ item.sku }}</span>
                  </template>
                </v-autocomplete>

                <div v-if="settings.selectedTemplate !== 'copy'">
                  <div class="title">{{$t('bulk_upload_reference_language')}}</div>
                  <div class="my-2 hint">{{$t('bulk_upload_reference_language_hint')}}</div>
                  <v-autocomplete
                    class="mt-2"
                    solo
                    v-model="settings.defaultLanguage"
                    :items="languages"
                    item-value="key"
                    item-text="description"
                  >
                    <template v-slot:item="{ item, on, attrs }">
                      <v-list-item v-show="item.key != 'all'" v-on="on" v-bind="attrs">
                        <v-list-item-content>
                          <v-list-item-title>
                            {{ item.description }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-autocomplete>

                  <div class="title">{{$t('bulk_upload_available_languages')}}</div>
                  <div class="my-2 hint">{{$t('bulk_upload_available_languages_hint')}}</div>
                  <v-autocomplete
                    class="mt-2"
                    solo
                    v-model="settings.availableLanguages"
                    :search-input.sync="searchLanguages"
                    multiple
                    :items="languages"
                    item-value="key"
                    item-text="description"
                  >
                    <template v-slot:item="{ item, on, attrs }">
                      <v-list-item v-on="on" v-bind="attrs">
                        <v-checkbox v-model="attrs.inputValue"></v-checkbox>
                        <v-list-item-content>
                          <v-list-item-title>
                            {{ item.description }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-autocomplete>
                </div>

                <v-btn @click="downloadTemplate()" text color="primary"><v-icon class="mr-2">mdi-cloud-download</v-icon>{{ $t('bulk_upload_download_template')}}</v-btn>
                <div class="my-2 hint">{{$t('bulk_upload_download_template_hint')}}</div>
              </v-col>
              <v-col cols="5">
                <div class="file-upload-ctn ml-5">
                  <v-layout @click="clickOnFileInput($event, 'xls-file-input')" class="upload-btn" align-center justify-center column>
                    <div v-if="!file || !file.name" class="text-xs-center">
                      <v-icon x-large color="primary">mdi-cloud-upload</v-icon>
                      <div class="text-uppercase font-weight-bold">{{$t('bulk_upload_chose_file')}}</div>
                      <div class="format-hint">
                        {{$t('supported_formats')}}:<br>
                        .xls,.xlsx
                      </div>
                    </div>

                    <div v-if="file && file.name" class="text-xs-center">
                      <v-icon x-large color="primary">mdi-cloud-upload</v-icon>
                      <div class="text-uppercase font-weight-bold">{{$t('change_file')}}</div>
                      <div class="format-hint">
                        {{file.name}}
                      </div>
                    </div>
                  </v-layout>
                  <input v-show="false"  ref="xls-file-input" accept=".xls,.xlsx" @change="onFileChange($event)" type="file"/>
                  <div class="instructions-ctn py-2 px-5">
                    <div class="instructions-title font-weight-bold">{{$t('bulk_upload_how_to_use')}}</div>
                    <ul>
                      <li class="hint">{{ $t('bulk_upload_instructions_1') }}</li>
                      <li class="hint" v-html="$t('bulk_upload_instructions_2')"></li>
                      <li class="hint">{{ $t('bulk_upload_instructions_3') }}</li>
                      <li class="hint">
                        {{ $t('bulk_upload_upload_logic') }}
                        <ul>
                          <li class="hint" v-html="$t('bulk_upload_upload_logic_blank_cell')"></li>
                          <li class="hint" v-html="$t('bulk_upload_upload_logic_filled_cell')"></li>
                          <li class="hint" v-html="$t('bulk_upload_upload_logic_remove')"></li>
                        </ul>
                      </li>
                      <li class="hint">{{ $t('bulk_upload_instructions_5') }}</li>
                      <li class="hint" v-help-center-links v-html="$t('bulk_upload_instructions_6', { url: helpCenterLink[language] ? helpCenterLink[language] : helpCenterLink['en'] })"></li>
                    </ul>
                  </div>
                </div>
              </v-col>
            </div>
            <v-layout class="footer">
              <v-spacer grow></v-spacer>
              <v-flex shrink>
                <v-btn @click="readFile(file)" :disabled="!isFormValid || !fullProductListLoaded" :loading="!fullProductListLoaded" raised color="primary">{{ $t('next') }}</v-btn>
              </v-flex>
            </v-layout>
          </v-stepper-content>
          <v-stepper-content step="2">
            <div class="row ma-2" v-if="isFileEmpty">
              {{ $t('bulk_upload_no_update_in_file') }}
            </div>
            <div v-if="!isFileEmpty">
              <div class="row ma-0">
                <v-col cols="2" class="overview-title font-weight-bold py-2 px-0" shrink>{{$t('bulk_upload_overview')}}</v-col>
                <v-col grow>
                  <v-row class="ma-0">
                    <div class="label mr-2">{{ $t('bulk_upload_template_version') }}</div>
                    <div class="font-weight-bold">V {{uploadedTemplateVersion}}
                      <span class="outdated-template-ctn" v-if="isOutdatedTemplate" v-html="$t('bulk_upload_outdated_template', { url: templateUrl, version: latestTemplateVersion })"></span>
                    </div>
                  </v-row>
                  <v-row class="ma-0">
                    <div class="label mr-2">{{ $t('bulk_upload_category') }}:</div>
                    <div v-if="settings.selectedTemplate.key" class="font-weight-bold">{{ $t(`bulk_upload_${settings.selectedTemplate.key}`) }}</div>
                    <div v-else class="font-weight-bold">{{ $t(`bulk_upload_${settings.templateElabel?.data?.published?.type}`) }}</div>
                  </v-row>
                  <v-row class="ma-0">
                    <div class="label mr-2">{{ $t('bulk_upload_input_language') }}:</div>
                    <div class="font-weight-bold"> {{ getLanguage(settings.defaultLanguage).description }}</div>
                  </v-row>
                  <v-row class="ma-0">
                    <div class="label mr-2">{{ $t('bulk_upload_published_languages') }}:</div>
                    <div class="font-weight-bold">
                      <span v-for="(lang, index) in settings.availableLanguages" :key="`available-lang-${lang}`">
                        <span class="cut-margin">{{getLanguage(lang).description}}</span>
                        <span class="comma" v-if="index < settings.availableLanguages.length - 1">, </span>
                      </span>
                    </div>
                  </v-row>
                </v-col>
              </div>
              <div class="delimiter"></div>
              <div class="row ma-0">
                <v-col cols="2" class="overview-title font-weight-bold px-0 py-6" shrink>{{$t('bulk_upload_file_summary')}}</v-col>
                <v-col grow>
                  <v-row class="ma-0 flex-nowrap" v-if="recap?.elabelToCreateCount">
                    <v-col cols="5" shrink class="file-summary-card new-elabel">
                      <div class="number font-weight-bold">
                        {{ $t("bulk_upload_new_elabel_count", {count: recap?.elabelToCreateCount }) }}
                      </div>
                      <div>{{ $t("bulk_upload_will_be_created") }}</div>
                    </v-col>
                    <v-col grow>
                      <v-switch class="mt-0" v-model="settings.assignCodeOnUpload" label="Assign a code to the new e-labels"></v-switch>
                      <div v-if="settings.assignCodeOnUpload">
                        <div>{{ $t('bulk_upload_create_batch_name') }}:</div>
                        <div>{{ $t('bulk_upload_batch_name_hint')}}</div>
                        <v-form v-model="batchFormValid" ref="batch-form">
                          <v-text-field class="mt-3 mb-6" :label="$t('bulk_upload_batch_name')" counter="50" :rules="batchRules" v-model="settings.uploadBatch"></v-text-field>
                        </v-form>
                      </div>
                    </v-col>
                  </v-row>
                  <div v-if="recap?.elabelToCreateCount" class="delimiter my-2"></div>
                  <v-row class="ma-0 flex-nowrap" v-if="recap?.elabelToUpdateCount">
                    <v-col cols="5" shrink class="file-summary-card update-elabel">
                      <div class="number font-weight-bold">
                        {{ $t("bulk_upload_update_elabel_count", {count: recap?.elabelToUpdateCount }) }}
                      </div>
                    </v-col>
                  </v-row>
                  <div v-if="recap?.elabelToUpdateCount" class="delimiter my-2"></div>
                  <v-row class="ma-0 flex-nowrap" v-if="recap?.productsNotFound?.length">
                    <v-col cols="5" shrink class="file-summary-card not-found">
                      <div class="number font-weight-bold">
                        {{ $t("bulk_upload_not_found_elabel_count", {count: recap?.productsNotFound?.length }) }}
                      </div>
                      <div>{{ $t("bulk_upload_elabel_not_found") }}</div>
                    </v-col>
                    <v-col grow>
                      <div>{{$t("bulk_upload_elabel_not_found_description")}}</div>
                      <span v-show="showAllNotFound == true || index < 20" v-for="(product, index) in recap?.productsNotFound" :key="`product-not-found-${index}`">
                        <span class="cut-margin font-weight-bold">{{product}}</span>
                        <span class="comma" v-if="index < recap?.productsNotFound.length - 1">, </span>
                      </span>
                      <div v-if="!showAllNotFound && recap?.productsNotFound.length > 20" @click="showAllNotFound = true">{{$t('view_all')}}</div>
                      <div v-if="showAllNotFound" @click="showAllNotFound = false">{{$t('view_less')}}</div>
                    </v-col>
                  </v-row>
                </v-col>
              </div>
            </div>
            <!-- <div v-if="!isFileEmpty && recap?.elabelToCreateCount > totalInactiveCodesCount" class="content mb-5">
              <div class="important font-weight-bold" >{{ $t('error') }}:</div>
              <div class="infos mt-1 font-weight-medium" >{{ $t('bulk_upload_quota_reached', { count: recap?.elabelToCreateCount, availableCount: totalInactiveCodesCount })}}</div>
              <div class="infos font-weight-medium" >{{ $t('bulk_upload_quota_reached_contact_support') }}</div>
            </div> -->
            <v-layout class="footer">
              <v-flex grow>
                <v-btn @click="step = 1" text color="primary">Back</v-btn>
              </v-flex>
              <v-flex shrink>
                <v-btn @click="startUpload()" :disabled="isFileEmpty || (!recap?.elabelToCreateCount && !recap?.elabelToUpdateCount)" raised color="primary">next</v-btn>
              </v-flex>
            </v-layout>
          </v-stepper-content>
          <v-stepper-content class="custom-stepper" step="3">
            <div class="loading" >
              <div align-center justify-center row fill-height>
                <v-flex shrink class="text-center infos font-weight-bold">
                  <span v-if="upload.loading && !upload.completed">{{$t('uploading')}}({{uploadPercent}}%)</span><br>
                  <span v-if="upload.completed">{{$t('upload_complete')}}</span><br>
                  <div class="progress-ctn">
                    <v-progress-linear color="#15BBF0" rounded background-color="#D9D9D9" v-show="upload.loading" :value="uploadPercent"></v-progress-linear>
                  </div>
                  <div v-if="this.upload.throttling && upload.loading" class="hint mt-3">{{$t('bulk_upload_uploading_hint')}}</div>
                  <div v-if="upload.completed" class="upload-recap font-weight-light mt-3">
                    <span v-if="recap.elabelToCreateCount && settings.assignCodeOnUpload">{{$t('bulk_upload_recap_created', { count: recap.elabelToCreateCount, batchName: settings.uploadBatch})}}</span><br>
                    <span v-if="recap.elabelToCreateCount && !settings.assignCodeOnUpload">{{$t('bulk_upload_recap_created_no_qr', { count: recap.elabelToCreateCount, batchName: settings.uploadBatch})}}</span><br>
                    <span v-if="recap.elabelToUpdateCount">{{$t('bulk_upload_recap_updated', { count: recap.elabelToUpdateCount}) }}</span><br>
                  </div>
                </v-flex>
              </div>
            </div>
            <v-layout class="footer" row>
              <v-spacer v-if="this.upload.completed && !this.settings.uploadBatch"></v-spacer>
              <v-btn v-if="this.upload.completed" @click="goToProducts()" :text="this.settings.uploadBatch.length > 0" :raised="!this.settings.uploadBatch.length" color="primary">back to products</v-btn>
              <v-spacer v-if="this.upload.completed && !upload.errors.length && this.settings.uploadBatch"></v-spacer>
              <v-btn v-if="this.upload.completed && !upload.errors.length && this.settings.uploadBatch" @click="downloadBatch" raised color="primary">download this batch</v-btn>
            </v-layout>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>
    </div>
  </div>
</template>

<script>

import { read, utils } from 'xlsx'
import Utils from '@/utils/utils'
import Api from '@/api'

import {
  isRemoveKeyword,
  stripRemoveKeywords,
  REMOVE_FIELD_FLAG,
  CLEAR_LIST_FLAG,
  CLEAR_OBJECT_FLAG,
  CLEAR_DEFAULT_VALUE_FLAG,
  isRemoveAllRow,
  isRemoveAllLinksRow,
  collectSkusToClear,
  applyFlaggedRemovals,
  dropEmptiedEntries
} from '@/utils/elabel-removals'

import {
  PENDING_NUTRIENTS_KEY,
  buildNutrientUpdates,
  applyNutrientUpdates,
  isNutrientRemovable
} from '@/utils/nutrition-updates'

import { rebuildCategoryLabels } from '@/utils/ingredient-category-labels'

import { applyIngredientNameTranslations } from '@/utils/ingredient-name-translations'

import Languages from '@/const/languages'
import Countries from '@/const/countries'
import NutritionInfo from '@/const/nutrition-info'
import BaseGrapevineProducts from '@/const/base_grapevine_products'
import RemyWineTemplate from '@/const/templates/remycointreau_wine.json'
import RemySpiritTemplate from '@/const/templates/remycointreau_spirit.json'

import WineTemplate from '@/const/templates/wine.json'
import SpiritTemplate from '@/const/templates/spirit.json'
import AromatisedWineTemplate from '@/const/templates/aromatised_wine.json'
import FoodAndBeverageTemplate from '@/const/templates/food_and_beverage.json'
import { mapGetters, mapMutations } from 'vuex'

export default {
  name: 'BulkUpload',
  computed: {
    ...mapGetters(['currentPlan', 'language', 'fullProductList', 'totalActiveCodesCount', 'selectedCampaign', 'products', 'gallery']),

    languages () {
      const languages = []
      languages.push({ key: 'all', description: this.$t('languages_all') })
      Languages.forEach((lang) => {
        languages.push({ key: lang.key, description: this.$t(`languages_${lang.key}`) })
      })
      return languages
    },

    templates () {
      return this.currentPlan && this.currentPlan.id && this.availableTemplates[this.currentPlan.id] ? this.availableTemplates[this.currentPlan.id] : this.availableTemplates.default
    },

    isFileEmpty () {
      return !this.recap?.elabelToCreateCount && !this.recap?.elabelToUpdateCount && this.recap.productsNotFound?.length === 0
    },

    isFormValid () {
      return this.file?.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' && !!this.settings.defaultLanguage && (this.settings.selectedTemplate.key || (this.settings.selectedTemplate === 'copy' && this.settings.templateElabel?.id))
    },

    uploadPercent () {
      if (this.upload.step === 1) {
        return Math.round(Math.round(this.upload.elabelLoadedCount / this.elabelToLoad.length * 100) / 3)
      } else if (this.upload.step === 2) {
        return 33 + Math.round(Math.round(this.upload.eLabelUpdated / (this.recap.elabelToCreateCount + this.recap.elabelToUpdateCount) * 100) / 3)
      } else if (this.upload.step === 3) {
        return 66 + Math.round(Math.round(this.upload.eLabelUpdated / (this.recap.elabelToCreateCount + this.recap.elabelToUpdateCount) * 100) / 3)
      }
      return 0
    },

    filteredProducts () {
      const searchedOrAllProducts = this.searchProduct ? this.products : this.fullProductList
      return searchedOrAllProducts.filter((product) => product.elabel && product.elabel.is_published)
    }
  },
  data () {
    return {
      latestTemplateVersion: '1.2.4',
      uploadedTemplateVersion: '1.0.0',
      templateUrl: Api.elabel_template_url,
      fullProductListLoaded: false,
      isOutdatedTemplate: false,
      showAllNotFound: false,
      removals: {},
      isLoadingProduct: false,
      searchProduct: '',
      debouncedSearch: null,
      searchLanguages: '',
      settings: {
        selectedTemplate: {},
        defaultLanguage: 'en',
        availableLanguages: ['all'],
        assignCodeOnUpload: true,
        uploadBatch: '',
        templateProduct: {},
        templateElabel: {}
      },
      batchFormValid: true,
      batchRules: [
        v => v.length <= 50 || this.$t('bulk_upload_batch_name_length'),
        v => !!v || this.$t('bulk_upload_batch_name_required')
      ],
      recap: {},
      upload: {
        loading: false,
        eLabelUpdated: 0,
        codesAssigned: 0,
        elabelLoadedCount: 0,
        throttling: false,
        completed: false,
        errors: [],
        step: 1
      },
      step: 1,
      file: {},
      batchSize: 10,
      createCount: 0,
      productsNotFound: [],
      productInfoSheet: [],
      ingredientsSheet: [],
      tutorialSheet: [],
      nutritionSheet: [],
      brandInformationSheet: [],
      pictogramsSheet: [],
      italianRecyclingSheet: [],
      spanishRecyclingSheet: [],
      packagingInformationSheet: [],
      settingsSheet: [],
      translatableFieldsSheet: [],
      countrySpecificSheet: [],
      // Per-language ingredient names from the Translatable Fields sheet, keyed by SKU then by name.
      pendingIngredientNameTranslations: {},
      elabelToLoad: [],
      elabelToUpdate: [],
      loadedElabel: {},
      productsToUpdate: {},
      alcoholTypes: ['wine', 'aromatised_wine', 'spirit'],
      availableTemplates: {
        'remycointreau-enterprise': [
          { key: 'wine', data: RemyWineTemplate },
          { key: 'spirit', data: RemySpiritTemplate }
        ],
        'remycointreau-enterprise-qr-manager': [
          { key: 'wine', data: RemyWineTemplate },
          { key: 'spirit', data: RemySpiritTemplate }
        ],
        default: [
          { key: 'wine', data: WineTemplate },
          { key: 'aromatised_wine', data: AromatisedWineTemplate },
          { key: 'spirit', data: SpiritTemplate },
          { key: 'food_and_beverage', data: FoodAndBeverageTemplate }
        ]
      },
      helpCenterLink: {
        en: 'https://help.scantrust.com/hc/en-us/articles/9300616722716-e-label-Bulk-Upload-Product-Information-Data-Bulk-Upload-Enterprise',
        es: 'https://help.scantrust.com/hc/es-419/articles/9300616722716-e-Label-Carga-masiva-Informaci%C3%B3n-del-producto-Carga-masiva-de-datos-de-e-label-Enterprise',
        de: 'https://help.scantrust.com/hc/de/articles/9300616722716-e-label-Massen-Upload-Produktinformationen-Bulk-Upload-von-e-label-Daten-Enterprise',
        fr: 'https://help.scantrust.com/hc/fr/articles/9300616722716-e-Label-Transfert-group%C3%A9-Informations-produits-T%C3%A9l%C3%A9chargement-group%C3%A9-de-donn%C3%A9es-d-e-label-Entreprise',
        it: 'https://help.scantrust.com/hc/it/articles/9300616722716-e-label-Caricamento-collettivo-Informazioni-sul-prodotto-Caricamento-in-blocco-di-dati-Enterprise',
        pt: 'https://help.scantrust.com/hc/pt/articles/9300616722716-e-e-label-Upload-em-massa-Informa%C3%A7%C3%A3o-do-produto-Upload-em-massa-de-dados-de-e-label-Empresa'
      }
    }
  },
  watch: {
    async searchProduct (val) {
      if (!val) {
        this.isLoadingProduct = false
        return
      }
      this.debouncedSearch(val)
    },

    'settings.availableLanguages' (newVal, oldVal) {
      this.searchLanguages = ''
      const allIndex = newVal.indexOf('all')
      if (!newVal.length || (~allIndex && newVal.length > 1 && !~oldVal.indexOf('all'))) {
        this.settings.availableLanguages = ['all']
      } else if (~allIndex && newVal.length > 1) {
        this.settings.availableLanguages.splice(allIndex, 1)
      }
    },
    '$route.params' () {
      if (this.$route.name === 'bulk-upload') {
        this.resetState()
      }
    },

    async 'settings.templateProduct' (newVal, oldVal) {
      if (newVal === null || (typeof newVal === 'object' && !Object.keys(newVal).length)) {
        this.searchProduct = ''
        this.settings.templateElabel = {}
      }
      if (newVal?.id && newVal?.elabel?.id) {
        // load elabel data
        this.settings.templateElabel = await this.$store.dispatch('loadElabel', { sku: newVal.sku, id: newVal.id })
        // Some older e-labels have no language in their published data. Fall back to the one the
        // editor holds, then to English, so the upload never files values under an empty language.
        const templateData = this.settings.templateElabel.data
        this.settings.defaultLanguage = templateData.published?.default_language || templateData.editor?.default_language || 'en'
      }
    }
  },
  methods: {
    ...mapMutations({
      setTotalActiveCount: 'SET_TOTAL_ACTIVE_CODES_COUNT',
      clearFilters: 'CLEAR_ACTIVE_CODE_FILTERS'
    }),

    getLanguage (lang) {
      return this.languages.find((l) => {
        return l.key === lang
      }) || { key: lang, description: '' }
    },

    downloadTemplate () {
      window.open(this.templateUrl, '_blank')
    },

    resetState () {
      console.log('resseting state', this.settings)
      this.step = 1
      this.upload = {
        loading: false,
        eLabelUpdated: 0,
        codesAssigned: 0,
        elabelLoadedCount: 0,
        throttling: false,
        completed: false,
        errors: [],
        step: 1
      }
      this.file = null
      if (this.$refs['xls-file-input']) {
        this.$refs['xls-file-input'].value = ''
      }
      this.recap = {}
      this.settings = {
        selectedTemplate: {},
        defaultLanguage: 'en',
        availableLanguages: ['all'],
        assignCodeOnUpload: true,
        uploadBatch: '',
        templateProduct: {},
        templateElabel: {}
      }

      this.productInfoSheet = []
      this.pictogramsSheet = []
      this.ingredientsSheet = []
      this.nutritionSheet = []
      this.brandInformationSheet = []
      this.italianRecyclingSheet = []
      this.spanishRecyclingSheet = []
      this.packagingInformationSheet = []
      this.settingsSheet = []
      this.translatableFieldsSheet = []
      this.pendingIngredientNameTranslations = {}
      this.elabelToLoad = []
      this.elabelToUpdate = []
      this.loadedElabel = {}
      this.productsToUpdate = {}
      this.removals = {}

      this.searchProduct = ''
      this.searchLanguages = ''
      this.showAllNotFound = false
      this.$store.dispatch('loadProducts', { search: '', campaign: this.selectedCampaign.id })
    },

    downloadBatch () {
      const filter = {
        name: 'Upload Batch',
        key: 'scantrust_upload_batch',
        value: this.settings.uploadBatch,
        searchKey: 'scm_scantrust_upload_batch',
        searchValue: this.settings.uploadBatch
      }

      this.clearFilters()
      this.$store.dispatch('addCodeFilter', filter)
      this.$store.dispatch('loadCodes', { campaign: this.selectedCampaign.id })
      this.resetState()
      this.$router.push({ name: 'codes' })
    },

    goToProducts () {
      this.resetState()
      this.$nextTick(() => {
        this.$router.push({ name: 'products' })
      })
    },

    clickOnFileInput (event, inputName) {
      if (event) {
        event.stopImmediatePropagation()
      }
      this.$refs[inputName].click()
    },

    async assignCodesBatch (index, isRetry) {
      const promiseUpdateCodesArray = []

      // Take next BatchSize Products
      let array = JSON.parse(JSON.stringify(this.elabelToUpdate.filter((item) => {
        return item.assignCode === true
      })))
      array = array.splice(index, this.batchSize)

      if (!array.length) {
        // this.completed = true
        this.upload.loading = false
        this.setTotalActiveCount(this.totalActiveCodesCount + this.createCount)
        return
      }

      // Should never be a problem for enterprise users (unlimited), maybe add back later if we open the feature to more plans
      // if (this.inactiveCodes.length < array.length) {
      //   await this.$store.dispatch('loadInactiveCodes')
      //   if (!this.inactiveCodes.length) {
      //     this.upload.completed = true
      //     this.upload.errors.push('Maximum number of codes reached. Contact support to increase the number of codes available.')
      //     this.setTotalActiveCount(this.totalActiveCodesCount + this.createCount)
      //   }
      // }
      array.forEach((item, promiseIndex) => {
        const tags = {}
        if (this.settings.uploadBatch) {
          tags.scantrust_upload_batch = this.settings.uploadBatch
        }

        if (!isRetry) {
          this.createCount++
        }
        promiseUpdateCodesArray.push(this.$store.dispatch('generateOnDemandCode', { product: item.product, tags }))
      })

      return new Promise((resolve, reject) => {
        Promise.all(promiseUpdateCodesArray).then(async (res) => {
          this.upload.codesAssigned += promiseUpdateCodesArray.length
          resolve(this.assignCodesBatch(index + this.batchSize))
        }).catch((err) => {
          if (err && err.response && err.response.status === 429) {
            this.upload.throttling = true
            console.log('Throttling assign code')
            setTimeout(() => {
              resolve(this.assignCodesBatch(index, true))
            }, 10000)
          }
        })
      })
    },

    async loadElabels (index) {
      const batch = []
      let array = JSON.parse(JSON.stringify(this.elabelToLoad))
      array = array.splice(index, this.batchSize)
      if (!array.length) {
        this.upload.elabelLoadedCount = this.elabelToLoad.length
        console.log('loading done')
        console.log(this.loadedElabel)
        return
      }

      array.forEach((item) => {
        batch.push(this.$store.dispatch('loadElabel', { sku: item.product.sku, id: item.product.id }))
      })

      return new Promise((resolve, reject) => {
        Promise.all(batch).then((data) => {
          this.upload.elabelLoadedCount += data.length
          data.forEach((response) => {
            this.loadedElabel[response.sku] = response.data.editor
          })
          resolve(this.loadElabels(index + batch.length))
        }).catch((err) => {
          if (err && err.response && err.response.status === 429) {
            this.upload.throttling = true
            console.log('Throttling load elabel')
            setTimeout(() => {
              resolve(this.loadElabels(index))
            }, 10000)
          }
        })
      })
    },

    async updateElabels (index) {
      const batch = []
      let array = JSON.parse(JSON.stringify(this.elabelToUpdate))
      array = array.splice(index, this.batchSize)

      if (!array.length) {
        console.log('update done')
        console.log('Final uploaded data:', this.productsToUpdate)
        return
      }

      const allLanguages = []
      Languages.forEach((lang) => {
        allLanguages.push(lang.key)
      })

      array.forEach((item) => {
        //  add default template info, merge with array item
        let elabelObj = {}
        console.log('FOR EACh _>', this.productsToUpdate, item.product.sku)

        if (this.settings.selectedTemplate === 'copy') {
          const defaultObj = JSON.parse(JSON.stringify(this.settings.templateElabel.data.published))

          // Filter out optional nutrients from source template if copying from alcohol product
          if (this.isType(this.alcoholTypes) && defaultObj?.nutrition?.table) {
            defaultObj.nutrition.table = defaultObj.nutrition.table.filter(nutrient => {
              const nutrientInfo = NutritionInfo.find(n => n.key === nutrient.key)
              if (!nutrientInfo) return true
              return !nutrientInfo.is_optional
            })
          }

          const defaultObjSections = [...defaultObj.sections]
          elabelObj = Utils.mergeDeep(defaultObj, this.productsToUpdate[item.product.sku])
          elabelObj.sections = defaultObjSections // Ensure sections matches the target template's

          // Done after the merge so the copied product's empty entries are not brought back in.
          elabelObj.default_language = this.settings.defaultLanguage
          elabelObj.available_languages = (elabelObj.available_languages || []).filter(Boolean)
          elabelObj.reviewed_languages = (elabelObj.reviewed_languages || []).filter(Boolean)
          if (!elabelObj.available_languages.includes(this.settings.defaultLanguage)) {
            elabelObj.available_languages.push(this.settings.defaultLanguage)
            elabelObj.reviewed_languages.push(this.settings.defaultLanguage)
          }
        } else {
          const defaultObj = JSON.parse(JSON.stringify(this.settings.selectedTemplate.data))
          defaultObj.default_language = this.settings.defaultLanguage
          if (~this.settings.availableLanguages.indexOf('all')) {
            defaultObj.available_languages = allLanguages
            defaultObj.reviewed_languages = allLanguages
          } else {
            defaultObj.available_languages = this.settings.availableLanguages
            defaultObj.reviewed_languages = this.settings.availableLanguages
          }
          const defaultObjSections = [...defaultObj.sections]
          elabelObj = Utils.mergeDeep(defaultObj, this.productsToUpdate[item.product.sku])
          elabelObj.sections = defaultObjSections
        }

        // Must run before applyFlaggedRemovals so the collected changes never reach it: the
        // merged data carries no nutrition table, so writing the provided values into the
        // stored table here is what makes a nutrient nobody mentioned keep its existing value.
        applyNutrientUpdates(elabelObj, { isFoodAndBeverage: this.isType(['food_and_beverage']), isAlcoholType: this.isType(this.alcoholTypes), nutritionInfoList: NutritionInfo })

        // Must run before applyFlaggedRemovals so a #REMOVE cell is deleted like any other flag.
        const ingredientNameTranslations = this.pendingIngredientNameTranslations[item.product.sku]
        if (ingredientNameTranslations && elabelObj.ingredients) {
          applyIngredientNameTranslations({
            lists: [elabelObj.ingredients.list, elabelObj.ingredients.aromatised_wine_list],
            translationsByName: ingredientNameTranslations,
            defaultLanguage: elabelObj.default_language || this.settings.defaultLanguage
          })
        }

        // Turn the removal flags into actual deletions after every merge is done.
        // Runs the same way for the create and update paths; on the create path a
        // flag only ever clears a template default.
        applyFlaggedRemovals(elabelObj)
        if (this.removals[item.product.sku]) {
          dropEmptiedEntries(elabelObj)
        }

        // Rebuild the custom-category translations from the merged lists. This prevents a renamed
        // category from keeping its old entry, because mergeDeep merges this map while replacing
        // the lists.
        if (elabelObj.ingredients) {
          const { labels, changed } = rebuildCategoryLabels({
            lists: [elabelObj.ingredients.list, elabelObj.ingredients.aromatised_wine_list],
            existingLabels: elabelObj.ingredients.category_labels,
            defaultLanguage: elabelObj.default_language || this.settings.defaultLanguage,
            languageKeys: Languages.map((language) => language.key)
          })
          if (changed) {
            if (labels) elabelObj.ingredients.category_labels = labels
            else delete elabelObj.ingredients.category_labels
          }
        }

        if (elabelObj['responsible-consumption'] && !this.isType(['wine', 'aromatised_wine'])) {
          delete elabelObj['responsible-consumption'].responsible_drinking_logo
          delete elabelObj['responsible-consumption'].show_responsible_message
        }

        batch.push(this.$store.dispatch('updateElabel', { name: item.product.sku, id: item.product.id, data: { editor: elabelObj, published: elabelObj }, published: true }))
      })

      return new Promise((resolve, reject) => {
        Promise.all(batch).then((data) => {
          this.upload.eLabelUpdated += batch.length
          resolve(this.updateElabels(index + batch.length))
        }).catch((err) => {
          if (err && err.response && err.response.status === 429) {
            this.upload.throttling = true
            console.log('Throttling udp elabel')
            setTimeout(() => {
              this.updateElabels(index)
            }, 10000)
          }
        })
      })
    },

    async startUpload () {
      if (this.recap?.elabelToCreateCount && this.settings.assignCodeOnUpload) {
        this.$refs['batch-form'].validate()
        if (!this.batchFormValid) {
          return
        }
      }

      this.upload.loading = true
      this.step = 3
      this.upload.step = 1
      if (this.elabelToLoad.length) {
        this.loadElabels(0).then((res) => {
          this.upload.step = 2
          for (const productSku in this.productsToUpdate) {
            // Rough merge for now, advanced merge later
            // this.productsToUpdate[productSku] = { ...this.loadedElabel[productSku], ...this.productsToUpdate[productSku] }
            // if (this.settings.templateElabel && this.settings.selectedTemplate === 'copy') {
            //   this.productsToUpdate[productSku] = Utils.mergeDeep(this.settings, this.productsToUpdate[productSku])
            // } else {
            if (this.loadedElabel[productSku]) {
              if (this.settings.selectedTemplate !== 'copy') {
                const loadedElabelCopy = JSON.parse(JSON.stringify(this.loadedElabel[productSku] || {}))
                // Drop the loaded e-label's language settings so that the UI's language dropdowns control it instead
                delete loadedElabelCopy.reviewed_languages
                delete loadedElabelCopy.available_languages
                delete loadedElabelCopy.default_language

                // Food and beverage labels have no structured ingredient list, so dropping the stored
                // one prevents a label from keeping ingredients its own editor cannot open.
                if (this.settings.selectedTemplate?.data?.type === 'food_and_beverage' && loadedElabelCopy.ingredients) {
                  delete loadedElabelCopy.ingredients.list
                  delete loadedElabelCopy.ingredients.use_free_form
                }

                let defaultObj = JSON.parse(JSON.stringify(this.settings.selectedTemplate.data))
                // Backfill with existing e-label data (preserves non-empty values)
                defaultObj = Utils.mergeDeep(defaultObj, loadedElabelCopy)
                // Apply spreadsheet changes on top
                this.productsToUpdate[productSku] = Utils.mergeDeep(defaultObj, this.productsToUpdate[productSku])

                // Ensure template type is set
                if (this.settings.selectedTemplate?.data?.type) {
                  this.productsToUpdate[productSku].type = this.settings.selectedTemplate.data.type
                }
              }
            }
          }
          this.elabelToUpdate.forEach((elabel) => {
            elabel.assignCode = this.settings.assignCodeOnUpload
          })
          this.elabelToLoad.forEach((sku) => {
            this.elabelToUpdate.push({ product: sku.product, assignCode: false })
          })
          this.updateElabels(0).then((res) => {
            this.upload.step = 3
            this.assignCodesBatch(0).then((res) => {
              this.upload.completed = true
            })
          })
        })
      } else {
        this.upload.step = 2
        this.elabelToUpdate.forEach((elabel) => {
          elabel.assignCode = this.settings.assignCodeOnUpload
        })
        this.updateElabels(0).then((res) => {
          this.upload.step = 3
          this.assignCodesBatch(0).then((res) => {
            this.upload.completed = true
          })
        })
      }
    },

    parseFile () {
      this.elabelToLoad = []
      this.elabelToUpdate = []
      this.productsNotFound = []
      for (const product in this.productsToUpdate) {
        const found = this.fullProductList.findIndex((p) => {
          return p.sku === product
        })

        if (~found) {
          if (this.fullProductList[found].elabel && this.fullProductList[found].elabel.id) {
            this.elabelToLoad.push({ product: this.fullProductList[found] })
          } else if (!this.fullProductList[found].elabel?.id) {
            // If no elabel, we'll assign a code if they selected
            this.elabelToUpdate.push({ product: this.fullProductList[found], assignCode: true })
          }
          // else {
          //   // Go To create/update directly
          //   this.elabelToUpdate.push({ product: this.fullProductList[found], assignCode: false })
          // }
        } else {
          this.productsNotFound.push(product)
        }
      }
      this.recap.elabelToUpdateCount = this.elabelToLoad.length || 0
      this.recap.elabelToCreateCount = this.elabelToUpdate.length || 0
      this.recap.productsNotFound = this.productsNotFound
      if (!~this.settings.availableLanguages.indexOf('all') && !~this.settings.availableLanguages.indexOf(this.settings.defaultLanguage)) {
        this.settings.availableLanguages.push(this.settings.defaultLanguage)
      }
      this.step = 2
      // this.updateElabels(0).then((res) => {
      //   console.log('ALL DONE UPD', res)
      // })
      // E-label endpoint
      // await ELabelService.saveELabel({ id: target.id, name: target.sku, data: { editor: template, published: {} } })
      // Upload process
      // 1- Fetch product based on keys in productToUpdate
      // - if Product does not exist -> Add to skip list & ignore
      // - else try to load e-label
      //  If exist -> Merge existing & new object. Do we check for type == selected type? Or do we override
      //  If does not exist -> Fill gap with default template, push
      //
    },

    hasProvidedValue (value) {
      return value !== undefined && value !== null && value !== '' && value !== '#N/A' && value !== '#VALUE!'
    },

    // The removals map only controls whether dropEmptiedEntries runs in updateElabels —
    // nothing on screen shows it since the recap deletions block was removed. The labels
    // are kept in case we bring back a confirmation step.
    recordRemoval (sku, label) {
      if (!this.removals[sku]) {
        this.$set(this.removals, sku, [])
      }
      if (!this.removals[sku].includes(label)) {
        this.removals[sku].push(label)
      }
    },

    // Checks every spreadsheet cell that can hold the field (the visible one and its _key
    // column); on a #REMOVE, writes the removal flag and records it. Fields holding one
    // value per language pass CLEAR_OBJECT_FLAG and the portion fields pass
    // CLEAR_DEFAULT_VALUE_FLAG, so both are emptied rather than taken away.
    applyFieldRemoval (entry, cellKeys, targetObject, targetKey, removalLabel, removalFlag = REMOVE_FIELD_FLAG) {
      const keywordFound = cellKeys.some((cellKey) => isRemoveKeyword(entry[cellKey]))
      if (!keywordFound) return false
      targetObject[targetKey] = removalFlag
      this.recordRemoval(String(entry.sku).trim(), removalLabel)
      return true
    },

    // A cell containing only whitespace must behave like an empty cell. sheet_to_json omits
    // empty cells from its row objects, so removing these keys gives the exact same shape.
    // Drops cells holding nothing but spaces, and trims the columns that carry an
    // identifier rather than text. A stray space in one of those (the Andorra entry in the
    // template's lookup table was "AD ") would otherwise be saved into the e-label as-is.
    cleanCellValues (rows) {
      rows.forEach((row) => {
        for (const cellKey in row) {
          if (typeof row[cellKey] !== 'string') continue
          if (!row[cellKey].trim()) {
            delete row[cellKey]
          } else if (cellKey.endsWith('_key') || cellKey.startsWith('internal_')) {
            row[cellKey] = row[cellKey].trim()
          }
        }
      })
      return rows
    },

    initTranslatedStringAttribute (entry, elabelObject, spreadsheetKey, spreadsheetValue, eLabelKey) {
      if (entry[spreadsheetKey] && entry[spreadsheetKey] !== '#N/A' && entry[spreadsheetKey] !== '#VALUE!') {
        elabelObject[eLabelKey] = entry[spreadsheetKey]
      } else {
        elabelObject[eLabelKey] = {}
        elabelObject[eLabelKey][this.settings.defaultLanguage] = entry[spreadsheetValue]
      }
    },

    // initELabelObject (entry) {
    //   this.productsToUpdate[entry.sku] = JSON.parse(JSON.stringify(this.settings.selectedTemplate))
    //   this.productsToUpdate[entry.sku].default_language = this.defaultLanguage
    //   this.productsToUpdate[entry.sku].available_languages = [this.defaultLanguage]
    //   this.productsToUpdate[entry.sku].reviewed_languages = [this.defaultLanguage]
    // },

    parseToFloat (input) {
      const normalizedInput = typeof input === 'string' ? input.replace(',', '.') : input
      const number = parseFloat(normalizedInput)
      if (!isNaN(number)) {
        return number
      }
      return null
    },

    isType (types) {
      const type = this.settings.selectedTemplate === 'copy' ? this.settings.templateElabel?.data?.published?.type : this.settings.selectedTemplate.key
      return types.includes(type)
    },

    isContainedCategory (category) {
      return ~['ingredient_category_stabilising_agents', 'ingredient_category_acidity_regulators'].indexOf(category)
    },

    processProductSheet () {
      this.productInfoSheet.forEach((entry) => {
        if (entry.sku) {
          let defaultTemplate
          if (this.settings.selectedTemplate === 'copy') {
            defaultTemplate = JSON.parse(JSON.stringify(this.settings.templateElabel.data.published))
          } else {
            defaultTemplate = JSON.parse(JSON.stringify(this.settings.selectedTemplate.data))
          }
          if (!Object.prototype.hasOwnProperty.call(this.productsToUpdate, entry.sku)) {
            // this.initELabelObject(entry)
            this.productsToUpdate[entry.sku] = {
              'product-infos': JSON.parse(JSON.stringify(defaultTemplate['product-infos']))
            }
            if (this.isType([...this.alcoholTypes, 'food_and_beverage'])) {
              this.productsToUpdate[entry.sku]['serving-infos'] = JSON.parse(JSON.stringify(defaultTemplate['serving-infos']))
            }
          }

          // Global fields (shared by all template types)
          if (!this.applyFieldRemoval(entry, ['name'], this.productsToUpdate[entry.sku]['product-infos'], 'product_name', 'product name', CLEAR_OBJECT_FLAG)) {
            if (this.hasProvidedValue(entry.name)) {
              this.productsToUpdate[entry.sku]['product-infos'].product_name[this.settings.defaultLanguage] = entry.name
            }
          }
          const originCell = entry.origin_key && entry.origin_key !== '#N/A' ? entry.origin_key : entry.origin
          if (isRemoveKeyword(originCell)) {
            this.productsToUpdate[entry.sku]['product-infos'].origin = CLEAR_LIST_FLAG
            this.recordRemoval(String(entry.sku).trim(), 'origin')
          } else if (this.hasProvidedValue(entry.origin_key) || this.hasProvidedValue(entry.origin)) {
            this.productsToUpdate[entry.sku]['product-infos'].origin =
              entry.origin_key && entry.origin_key !== '#N/A'
                ? [entry.origin_key]
                : entry.origin && entry.origin.indexOf(',') > -1
                  ? stripRemoveKeywords(entry.origin)
                  : entry.origin || ''
          }
          if (this.hasProvidedValue(entry.geographical_indication_description)) {
            this.productsToUpdate[entry.sku]['geographical-indications'] = this.productsToUpdate[entry.sku]['geographical-indications'] || { list: [] }
            if (this.productsToUpdate[entry.sku]['geographical-indications'].list.length === 0) {
              this.productsToUpdate[entry.sku]['geographical-indications'].list.push({})
            }
            if (!this.applyFieldRemoval(entry, ['geographical_indication_description'], this.productsToUpdate[entry.sku]['geographical-indications'].list[0], 'description', 'geographical indication')) {
              this.productsToUpdate[entry.sku]['geographical-indications'].list[0].description = entry.geographical_indication_description
            }
          }

          if (this.isType(this.alcoholTypes)) {
            let netQuantity = entry.net_quantity || entry.net_quantity_ml // 'net_quantity_ml' for legacy fallback
            if (isRemoveKeyword(netQuantity)) {
              this.productsToUpdate[entry.sku]['product-infos'].volumes = CLEAR_LIST_FLAG
              this.recordRemoval(String(entry.sku).trim(), 'net quantity')
            } else {
              if (netQuantity && typeof netQuantity !== 'string') {
                netQuantity = netQuantity + ''
              }
              if (netQuantity && netQuantity.indexOf(',') > -1) {
                this.productsToUpdate[entry.sku]['product-infos'].volumes = stripRemoveKeywords(netQuantity)
              } else {
                this.productsToUpdate[entry.sku]['product-infos'].volumes = netQuantity ? [netQuantity] : []
              }
            }
            if (!this.applyFieldRemoval(entry, ['producing_year'], this.productsToUpdate[entry.sku]['product-infos'], 'producing_year', 'producing year')) {
              if (this.hasProvidedValue(entry.producing_year)) {
                this.productsToUpdate[entry.sku]['product-infos'].producing_year = entry.producing_year
              }
            }
            const sweetness = entry.sweetness_key && entry.sweetness_key !== '#N/A' ? entry.sweetness_key : entry.sugar_content
            if (!this.applyFieldRemoval(entry, ['sweetness_key', 'sugar_content'], this.productsToUpdate[entry.sku]['product-infos'], 'wine_sweetness', 'sugar content')) {
              if (this.hasProvidedValue(sweetness)) {
                this.productsToUpdate[entry.sku]['product-infos'].wine_sweetness = sweetness
              }
            }
            if (!this.applyFieldRemoval(entry, ['grape_variety'], this.productsToUpdate[entry.sku]['product-infos'], 'vine_variety', 'grape variety')) {
              if (this.hasProvidedValue(entry.grape_variety)) {
                this.productsToUpdate[entry.sku]['product-infos'].vine_variety = entry.grape_variety
              }
            }
            if (!this.applyFieldRemoval(entry, ['vintage_year'], this.productsToUpdate[entry.sku]['product-infos'], 'vintage_year', 'vintage year')) {
              if (this.hasProvidedValue(entry.vintage_year)) {
                this.productsToUpdate[entry.sku]['product-infos'].vintage_year = entry.vintage_year
              }
            }
            const wineColour = entry.wine_colour_key && entry.wine_colour_key !== '#N/A' ? entry.wine_colour_key : entry.wine_colour
            if (!this.applyFieldRemoval(entry, ['wine_colour_key', 'wine_colour'], this.productsToUpdate[entry.sku]['product-infos'], 'wine_colour', 'wine colour')) {
              if (this.hasProvidedValue(wineColour)) {
                this.initTranslatedStringAttribute(entry, this.productsToUpdate[entry.sku]['product-infos'], 'wine_colour_key', 'wine_colour', 'wine_colour')
              }
            }
            const productionMethod = entry.production_method_key && entry.production_method_key !== '#N/A' ? entry.production_method_key : entry.production_method
            if (!this.applyFieldRemoval(entry, ['production_method_key', 'production_method'], this.productsToUpdate[entry.sku]['product-infos'], 'production_method', 'production method')) {
              if (this.hasProvidedValue(productionMethod)) {
                this.initTranslatedStringAttribute(entry, this.productsToUpdate[entry.sku]['product-infos'], 'production_method_key', 'production_method', 'production_method')
              }
            }
          }

          if (this.isType([...this.alcoholTypes, 'food_and_beverage'])) {
            if (!this.applyFieldRemoval(entry, ['portion_size', 'serving_size_ml'], this.productsToUpdate[entry.sku]['serving-infos'], 'serving', 'portion size', CLEAR_DEFAULT_VALUE_FLAG)) {
              let servingSize = entry.portion_size || entry.serving_size_ml // 'serving_size_ml' for legacy fallback
              if (this.hasProvidedValue(servingSize)) {
                servingSize += ''
                this.productsToUpdate[entry.sku]['serving-infos'].serving = {
                  default: servingSize.replaceAll(/\s/g, '') || 100
                }
              }
            }
            if (!this.applyFieldRemoval(entry, ['alcohol_volume'], this.productsToUpdate[entry.sku]['product-infos'], 'alcohol_volume', 'alcohol volume')) {
              const alcoholVolume = this.parseToFloat(entry.alcohol_volume)
              if (this.hasProvidedValue(alcoholVolume)) {
                this.productsToUpdate[entry.sku]['product-infos'].alcohol_volume = alcoholVolume
              }
            }
            if (!this.applyFieldRemoval(entry, ['bottled_msg_key'], this.productsToUpdate[entry.sku]['product-infos'], 'bottled_in_protected_atmosphere', 'bottled-in-atmosphere message')) {
              const bottledAtmosphere = entry.bottled_msg_key && entry.bottled_msg_key !== '#N/A' ? entry.bottled_msg_key : null
              if (this.hasProvidedValue(bottledAtmosphere)) {
                this.productsToUpdate[entry.sku]['product-infos'].bottled_in_protected_atmosphere = bottledAtmosphere
              }
            }
          }

          if (this.isType(['food_and_beverage'])) {
            if (!this.applyFieldRemoval(entry, ['net_quantity'], this.productsToUpdate[entry.sku]['product-infos'], 'volume_quantity', 'net quantity')) {
              if (this.hasProvidedValue(entry.net_quantity)) {
                this.productsToUpdate[entry.sku]['product-infos'].volume_quantity = entry.net_quantity
              }
            }
            if (!this.applyFieldRemoval(entry, ['net_quantity_unit'], this.productsToUpdate[entry.sku]['product-infos'], 'volume_unit', 'net quantity unit')) {
              const volumeUnit = entry.net_quantity_unit ? entry.net_quantity_unit.toUpperCase() : null
              if (this.hasProvidedValue(volumeUnit)) {
                this.productsToUpdate[entry.sku]['product-infos'].volume_unit = volumeUnit
              }
            }

            if (!this.applyFieldRemoval(entry, ['portion_unit'], this.productsToUpdate[entry.sku]['serving-infos'], 'unit', 'portion unit', CLEAR_DEFAULT_VALUE_FLAG)) {
              const portionUnit = entry.portion_unit ? entry.portion_unit.toUpperCase() : null
              if (this.hasProvidedValue(portionUnit)) {
                this.productsToUpdate[entry.sku]['serving-infos'].unit = {
                  default: portionUnit
                }
              }
            }
          }

          if (this.isType(['aromatised_wine'])) {
            if (!this.applyFieldRemoval(entry, ['complement_category_1_key'], this.productsToUpdate[entry.sku]['product-infos'], 'alcohol_content', 'alcohol content category')) {
              const alcoholContent = entry.complement_category_1_key && entry.complement_category_1_key !== '#N/A' ? entry.complement_category_1_key : null
              if (this.hasProvidedValue(alcoholContent)) {
                this.productsToUpdate[entry.sku]['product-infos'].alcohol_content = alcoholContent
              }
            }
            if (!this.applyFieldRemoval(entry, ['complement_category_2_key'], this.productsToUpdate[entry.sku]['product-infos'], 'sparkling_content', 'sparkling category')) {
              const sparklingContent = entry.complement_category_2_key && entry.complement_category_2_key !== '#N/A' ? entry.complement_category_2_key : null
              if (this.hasProvidedValue(sparklingContent)) {
                this.productsToUpdate[entry.sku]['product-infos'].sparkling_content = sparklingContent
              }
            }
          }

          const productType = entry.type_key && entry.type_key !== '#N/A' ? entry.type_key : entry.product_type
          if (!this.applyFieldRemoval(entry, ['type_key', 'product_type'], this.productsToUpdate[entry.sku]['product-infos'], 'type', 'product type', CLEAR_OBJECT_FLAG)) {
            if (this.hasProvidedValue(productType)) {
              this.initTranslatedStringAttribute(entry, this.productsToUpdate[entry.sku]['product-infos'], 'type_key', 'product_type', 'type')
            }
          }
        }
      })
    },

    processNutritionSheet () {
      this.nutritionSheet.forEach((entry) => {
        if (entry.sku) {
          if (!Object.prototype.hasOwnProperty.call(this.productsToUpdate, entry.sku)) {
            this.productsToUpdate[entry.sku] = {}
          }

          // Per-key merge, never a table: applyNutrientUpdates (called from updateElabels)
          // is the only place a stored nutrition table is ever read or written.
          this.productsToUpdate[entry.sku].nutrition = this.productsToUpdate[entry.sku].nutrition || {}

          if (entry.show_zero_values_as_negligible !== undefined) {
            this.productsToUpdate[entry.sku].nutrition.showZeroAsNegligible = entry.show_zero_values_as_negligible
          }
          if (entry.hide_section !== undefined && this.isType(['food_and_beverage'])) {
            this.productsToUpdate[entry.sku].nutrition.is_hidden = entry.hide_section
          }
          if (this.isType(['food_and_beverage'])) {
            if (entry.display_mode !== undefined) {
              this.productsToUpdate[entry.sku].nutrition.mode = entry.display_mode
            }
            if (entry.display_reference_intake !== undefined) {
              this.productsToUpdate[entry.sku].nutrition.display_reference_intake = entry.display_reference_intake
            }
          }

          const nutrientUpdates = buildNutrientUpdates(entry, { isRemoveKeyword })
          if (Object.keys(nutrientUpdates.set).length || nutrientUpdates.remove.length) {
            this.productsToUpdate[entry.sku].nutrition[PENDING_NUTRIENTS_KEY] = nutrientUpdates
            // Only record the removals that will actually go through (optional nutrients, or
            // ones left over from a different template type, wherever they sit in the table).
            // Both sides call isNutrientRemovable, so this can't drift out of step with what
            // applyNutrientUpdates ends up doing.
            nutrientUpdates.remove
              .filter((tableKey) => isNutrientRemovable(tableKey, { isFoodAndBeverage: this.isType(['food_and_beverage']), nutritionInfoList: NutritionInfo }))
              .forEach((tableKey) => this.recordRemoval(entry.sku, `nutrition: ${tableKey}`))
          }
        }
      })
    },

    processBrandInformationSheet () {
      this.brandInformationSheet.forEach(entry => {
        if (!entry.sku) return
        if (!Object.prototype.hasOwnProperty.call(this.productsToUpdate, entry.sku)) {
          this.productsToUpdate[entry.sku] = {}
        }

        this.productsToUpdate[entry.sku]['brand-infos'] =
        this.productsToUpdate[entry.sku]['brand-infos'] || {}
        const brandInfo = this.productsToUpdate[entry.sku]['brand-infos']

        if (isRemoveKeyword(entry.image_key)) {
          brandInfo.image = REMOVE_FIELD_FLAG
          this.recordRemoval(entry.sku, 'additional information image')
        } else if (entry.image_key) {
          const matchingImage = this.gallery.find((image) => image.key === entry.image_key.trim())
          if (matchingImage) {
            brandInfo.image = matchingImage.file
          }
        }

        if (isRemoveKeyword(entry.text_color)) {
          brandInfo.text_color = REMOVE_FIELD_FLAG
          this.recordRemoval(entry.sku, 'text color')
        } else if (entry.text_color) {
          brandInfo.text_color = entry.text_color
        }

        if (typeof entry.hide_learn_more_text === 'boolean') {
          brandInfo.hide_learn_more_text = entry.hide_learn_more_text
        } else if (typeof entry.hide_learn_more_text === 'string') {
          const v = entry.hide_learn_more_text.trim().toUpperCase()
          if (v === 'TRUE') brandInfo.hide_learn_more_text = true
          else if (v === 'FALSE') brandInfo.hide_learn_more_text = false
        }

        // Build link objects. link_1..3 restate the whole array on any fill, so a stray
        // #REMOVE typed into one of them is not a removal - it is treated as a blank cell
        // (never stored as a URL). Clearing all links is the dedicated remove_all_links flag.
        const removeAllLinks = isRemoveAllLinksRow(entry)
        const anyLinkCellFilled = [1, 2, 3].some((linkNumber) => {
          const rawLink = String(entry[`link_${linkNumber}`] ?? '').trim()
          return rawLink !== '' && !isRemoveKeyword(rawLink)
        })
        if (removeAllLinks && !anyLinkCellFilled) {
          brandInfo.links = CLEAR_LIST_FLAG
          this.recordRemoval(entry.sku, 'links (all entries)')
        } else {
          const maxLinks = 3
          brandInfo.links = []
          for (let i = 1; i <= maxLinks; i++) {
            const rawLink = String(entry[`link_${i}`] ?? '').trim()
            brandInfo.links.push({
              // a stray #REMOVE typed here is not a removal - treat it as an empty cell so it is never stored as a URL
              link: isRemoveKeyword(rawLink) ? '' : rawLink,
              text: {}
            })
          }
          while (brandInfo.links.length && !brandInfo.links[brandInfo.links.length - 1].link) {
            brandInfo.links.pop()
          }
        }

        const hideLinksForRegulatedCountries = entry.show_links_only_in_allowed_countries === true || (typeof entry.show_links_only_in_allowed_countries === 'string' &&
        entry.show_links_only_in_allowed_countries.toUpperCase() === 'TRUE') ||
        entry.show_links_only_in_allowed_countries == null
        if (hideLinksForRegulatedCountries && this.isType(['wine', 'aromatised_wine'])) {
          brandInfo.hide_links_for_countries = ['REGULATED_COUNTRIES']
        } else {
          const selectedCountryKeys = entry.hide_links_for_countries ? entry.hide_links_for_countries.split(',').map(s => s.trim().toUpperCase()).filter(Boolean) : []

          let keys
          if (selectedCountryKeys.includes('EU')) {
            keys = Countries.map((country) => country.key)
          } else {
            keys = selectedCountryKeys.filter(key => key !== 'NON_EU' && Countries.find(country => country.key.toUpperCase() === key))
          }

          if (selectedCountryKeys.includes('NON_EU')) {
            keys.push('NON_EU')
          }

          brandInfo.hide_links_for_countries = [...new Set(keys)]
        }
      })
    },

    processPictogramsSheet () {
      this.pictogramsSheet.forEach((entry) => {
        if (entry.sku) {
          if (!Object.prototype.hasOwnProperty.call(this.productsToUpdate, entry.sku)) {
            // this.initELabelObject(entry)
            this.productsToUpdate[entry.sku] = {}
          }
        } else {
          return
        }

        const product = this.productsToUpdate[entry.sku]

        // Handle default icons
        if (entry.internal_icon_id && entry.internal_icon_id !== '#N/A') {
          const prefix = entry.internal_icon_id.split(':')[0]
          const icon = entry.internal_icon_id.split(':')[1]
          if (prefix) {
            switch (prefix) {
              case 'allergens':
                product.ingredients = product.ingredients || {}
                product.ingredients.allergens_icons = product.ingredients.allergens_icons || []
                if (!product.ingredients.allergens_icons.includes(icon)) {
                  product.ingredients.allergens_icons.push(icon)
                }
                break
              case 'rc':
                product['responsible-consumption'] = product['responsible-consumption'] || { icons: [], custom_icons: [] }
                if (icon === 'responsible_drinking' && this.isType(['wine', 'aromatised_wine'])) {
                  product['responsible-consumption'].responsible_drinking_logo = true
                } else if (!product['responsible-consumption'].icons.includes(icon)) {
                  product['responsible-consumption'].icons.push(icon)
                }
                break
              case 'certifications':
                product.sustainability = product.sustainability || { organic_icons: [], recyclability_icons: [], certifications_icons: [], custom_icons: [] }
                if (!product.sustainability.certifications_icons.includes(icon)) {
                  product.sustainability.certifications_icons.push(icon)
                }
                break
              case 'recyclability':
                product.sustainability = product.sustainability || { organic_icons: [], recyclability_icons: [], certifications_icons: [], custom_icons: [] }
                if (!product.sustainability.recyclability_icons.includes(icon)) {
                  product.sustainability.recyclability_icons.push(icon)
                }
                break
              case 'organic':
                product.sustainability = product.sustainability || { organic_icons: [], recyclability_icons: [], certifications_icons: [], custom_icons: [] }
                if (!product.sustainability.organic_icons.includes(icon)) {
                  product.sustainability.organic_icons.push(icon)
                }
                break
              case 'geography':
                if (this.hasProvidedValue(icon)) {
                  product['geographical-indications'] = product['geographical-indications'] || { list: [] }
                  if (product['geographical-indications'].list.length === 0) {
                    product['geographical-indications'].list.push({})
                  }
                  if (this.isType(['food_and_beverage'])) {
                    product['geographical-indications'].list[0].custom_icons = product['geographical-indications'].list[0].custom_icons || []
                  }
                  product['geographical-indications'].list[0].icon = icon
                }
                break
            }
          }
        // Handle custom icons
        } else if (entry.icon_id) {
          const matchingImage = this.gallery.find((image) => image.key === entry.icon_id.trim())
          if (!matchingImage || !entry.section) return

          // entry.section examples: 'sustainability_recyclability', 'responsible-consumption', 'free-section_1'
          const section = entry.section.split('_')[0]
          const category = entry.section.split('_')[1]
          const countriesArr = entry.countries ? entry.countries.split(',').map(s => s.trim()) : ['all']

          if (section === 'free-section' && this.isType(['food_and_beverage'])) {
            product['free-sections'] = product['free-sections'] || { sections: [] }
            const sectionIndex = parseInt(category) - 1
            while (product['free-sections'].sections.length <= sectionIndex) {
              product['free-sections'].sections.push({
                title: {},
                custom_icons: [],
                description: {}
              })
            }

            const customIcon = {
              name: matchingImage.name,
              url: matchingImage.file,
              countries: [...new Set(countriesArr)],
              wide: ((entry.is_wide && typeof entry.is_wide === 'boolean') || (entry.is_wide && typeof entry.is_wide === 'string' && entry.is_wide.toLowerCase() === 'true')) || false
            }
            const freeSectionIcons = product['free-sections'].sections[sectionIndex].custom_icons
            if (!freeSectionIcons.some((existingIcon) => JSON.stringify(existingIcon) === JSON.stringify(customIcon))) {
              freeSectionIcons.push(customIcon)
            }
          } else {
            product[section] = product[section] || {}
            product[section].custom_icons = product[section].custom_icons || []
            const customIcon = {
              category,
              name: matchingImage.name,
              url: matchingImage.file,
              countries: [...new Set(countriesArr)],
              wide: ((entry.is_wide && typeof entry.is_wide === 'boolean') || (entry.is_wide && typeof entry.is_wide === 'string' && entry.is_wide.toLowerCase() === 'true')) || false
            }
            if (!product[section].custom_icons.some((existingIcon) => JSON.stringify(existingIcon) === JSON.stringify(customIcon))) {
              product[section].custom_icons.push(customIcon)
            }
          }
        }
      })
    },

    processIngredientsSheet () {
      const removalSkus = collectSkusToClear(this.ingredientsSheet)
      this.ingredientsSheet.forEach((entry) => {
        if (isRemoveAllRow(entry)) return
        if (entry.sku) {
          if (!Object.prototype.hasOwnProperty.call(this.productsToUpdate, entry.sku)) {
            // this.initELabelObject(entry)
            this.productsToUpdate[entry.sku] = {}
          }

          if (this.isType(['food_and_beverage'])) {
            this.productsToUpdate[entry.sku].ingredients = this.productsToUpdate[entry.sku].ingredients || {}
            // F&B templates only have free text
            this.productsToUpdate[entry.sku].ingredients.use_free_form = true
            return
          }

          if (!Object.prototype.hasOwnProperty.call(this.productsToUpdate[entry.sku], 'ingredients')) {
            this.productsToUpdate[entry.sku].ingredients = {}
            if (this.isType(this.alcoholTypes)) {
              this.productsToUpdate[entry.sku].ingredients.list = []
            }
            if (this.isType(['aromatised_wine'])) {
              this.productsToUpdate[entry.sku].ingredients.aromatised_wine_list = []
            }
          }
          this.productsToUpdate[entry.sku].ingredients.use_free_form = false

          const ingredient = {
            is_allergen: ((entry.is_allergen && typeof entry.is_allergen === 'boolean') || (entry.is_allergen && typeof entry.is_allergen === 'string' && entry.is_allergen.toLowerCase() === 'true')) || false,
            is_organic: ((entry.is_organic && typeof entry.is_organic === 'boolean') || (entry.is_organic && typeof entry.is_organic === 'string' && entry.is_organic.toLowerCase() === 'true')) || false
          }

          // This condition is for when a user enters only one custom value and no category. In that case we set main_category and take the ingredient name from the category field instead (due to the split per | )
          if ((entry.ingredient_name === '#VALUE!' && entry.ingredient_category === '#VALUE!') || (!entry.ingredient_name && !entry.ingredient_category)) {
            ingredient.category = 'ingredient_category_main_ingredients'
            this.initTranslatedStringAttribute(entry, ingredient, 'ingredient_key', 'ingredient', 'description')
          } else if (!entry.ingredient_name && entry.ingredient_category) {
            ingredient.category = 'ingredient_category_main_ingredients'
            this.initTranslatedStringAttribute(entry, ingredient, 'ingredient_key', 'ingredient_category', 'description')
          } else if (entry.ingredient_category_key && entry.ingredient_category_key !== '#N/A' && (entry.ingredient_key || entry.ingredient_name)) {
            ingredient.category = entry.ingredient_category_key
            this.initTranslatedStringAttribute(entry, ingredient, 'ingredient_key', 'ingredient_name', 'description')
          } else {
            // Trim the custom category so the Translatable Fields rows can match it by name.
            ingredient.category = typeof entry.ingredient_category === 'string'
              ? entry.ingredient_category.trim()
              : entry.ingredient_category
            this.initTranslatedStringAttribute(entry, ingredient, 'ingredient_key', 'ingredient_name', 'description')
          }

          if (this.isContainedCategory(ingredient.category)) {
            ingredient.is_contained_display = true
          }

          if (this.isType(['wine', 'spirit']) || (entry.aromatised_wine_key === false || !(entry.aromatised_base_wine_name && entry.aromatised_wine_key !== false))) {
            this.productsToUpdate[entry.sku].ingredients.list.push(ingredient)
          } else if (this.isType(['aromatised_wine'])) {
            this.productsToUpdate[entry.sku].ingredients.aromatised_wine_list.push(ingredient)
            if (entry.aromatised_wine_key === 'ingredient_wine' || BaseGrapevineProducts.includes(entry.aromatised_wine_key)) {
              this.productsToUpdate[entry.sku].ingredients.aromatised_wine_type = entry.aromatised_wine_key
            } else if (this.hasProvidedValue(entry.aromatised_base_wine_name)) {
              this.productsToUpdate[entry.sku].ingredients.aromatised_wine_type = {}
              this.productsToUpdate[entry.sku].ingredients.aromatised_wine_type[this.settings.defaultLanguage] = entry.aromatised_base_wine_name
            }
          }
        }
      })
      removalSkus.forEach((sku) => {
        if (!Object.prototype.hasOwnProperty.call(this.productsToUpdate, sku)) {
          this.productsToUpdate[sku] = {}
        }
        this.productsToUpdate[sku].ingredients = this.productsToUpdate[sku].ingredients || {}
        if (this.isType(['food_and_beverage'])) {
          this.productsToUpdate[sku].ingredients.free_text = CLEAR_OBJECT_FLAG
        } else {
          this.productsToUpdate[sku].ingredients.list = CLEAR_LIST_FLAG
          // Also clear any manual-mode free text and reset the mode to structured, matching
          // the template's own default (wine.json ships use_free_form:false) - otherwise a
          // wine product stuck in manual mode keeps its visible text through a remove-all.
          this.productsToUpdate[sku].ingredients.free_text = CLEAR_OBJECT_FLAG
          this.productsToUpdate[sku].ingredients.use_free_form = false
          if (this.isType(['aromatised_wine'])) {
            this.productsToUpdate[sku].ingredients.aromatised_wine_list = CLEAR_LIST_FLAG
            this.productsToUpdate[sku].ingredients.aromatised_wine_type = REMOVE_FIELD_FLAG
          }
        }
        this.recordRemoval(sku, 'ingredients (all entries)')
      })
    },

    processItalianRecyclingSheet () {
      const removalSkus = collectSkusToClear(this.italianRecyclingSheet)
      this.italianRecyclingSheet.forEach((entry) => {
        if (isRemoveAllRow(entry)) return
        if (entry.sku) {
          if (!Object.prototype.hasOwnProperty.call(this.productsToUpdate, entry.sku)) {
            // this.initELabelObject(entry)
            this.productsToUpdate[entry.sku] = {}
          }

          if (!Object.prototype.hasOwnProperty.call(this.productsToUpdate[entry.sku], 'sustainability')) {
            this.productsToUpdate[entry.sku].sustainability = {
              recyclability_icons: [],
              italian_recyclability: { list: [], is_enabled: true },
              certifications_icons: [],
              organic_icons: [],
              custom_icons: []
            }
          } else if (!this.productsToUpdate[entry.sku].sustainability.italian_recyclability?.list) {
            this.productsToUpdate[entry.sku].sustainability.italian_recyclability = { list: [], is_enabled: true }
          }

          const material = {}
          material.index = entry.numbering

          if (entry.custom_name) {
            material.key = entry.custom_name
          } else {
            material.key = entry.material_key
          }
          material.material = entry.material_translation_key
          material.packaging = entry.packaging_key
          material.collectionGuideline = entry.collection_key

          this.productsToUpdate[entry.sku].sustainability.italian_recyclability.list.push(material)
        }
      })
      removalSkus.forEach((sku) => {
        if (!Object.prototype.hasOwnProperty.call(this.productsToUpdate, sku)) {
          this.productsToUpdate[sku] = {}
        }
        this.productsToUpdate[sku].sustainability = this.productsToUpdate[sku].sustainability ||
          { organic_icons: [], recyclability_icons: [], certifications_icons: [], custom_icons: [] }
        this.productsToUpdate[sku].sustainability.italian_recyclability =
          this.productsToUpdate[sku].sustainability.italian_recyclability || {}
        this.productsToUpdate[sku].sustainability.italian_recyclability.list = CLEAR_LIST_FLAG
        this.recordRemoval(sku, 'Italian recycling (all entries)')
      })
    },

    processSpanishRecyclingSheet () {
      const removalSkus = collectSkusToClear(this.spanishRecyclingSheet)
      this.spanishRecyclingSheet.forEach((entry) => {
        if (isRemoveAllRow(entry)) return
        if (entry.sku) {
          if (!Object.prototype.hasOwnProperty.call(this.productsToUpdate, entry.sku)) {
            // this.initELabelObject(entry)
            this.productsToUpdate[entry.sku] = {}
          }

          if (!Object.prototype.hasOwnProperty.call(this.productsToUpdate[entry.sku], 'sustainability')) {
            this.productsToUpdate[entry.sku].sustainability = {
              recyclability_icons: [],
              spanish_recyclability: { list: [], is_enabled: true },
              certifications_icons: [],
              organic_icons: [],
              custom_icons: []
            }
          } else if (!this.productsToUpdate[entry.sku].sustainability.spanish_recyclability?.list) {
            this.productsToUpdate[entry.sku].sustainability.spanish_recyclability = { list: [], is_enabled: true }
          }

          const material = {
            packaging: '',
            labels: []
          }
          console.log('ENTRY', entry)

          if ((!entry.packaging_key || entry.packaging_key === '#N/A') && entry.packaging) {
            material.packaging = {}
            material.packaging[this.settings.defaultLanguage] = entry.packaging
          } else {
            material.packaging = entry.packaging_key
          }

          if (entry.internal_icon_id) {
            const icon = entry.internal_icon_id.split(':')[1]
            material.labels = [icon]
          }

          const index = this.productsToUpdate[entry.sku].sustainability.spanish_recyclability.list.findIndex((item) => {
            if (typeof item.packaging === 'string') {
              return item.packaging === material.packaging
            } else if (typeof item.packaging === 'object' && item.packaging[this.settings.defaultLanguage] && typeof material.packaging === 'object' && material.packaging[this.settings.defaultLanguage]) {
              return item.packaging[this.settings.defaultLanguage] === material.packaging[this.settings.defaultLanguage]
            }
            return false
          })

          if (index >= 0) {
            this.productsToUpdate[entry.sku].sustainability.spanish_recyclability.list[index].labels = this.productsToUpdate[entry.sku].sustainability.spanish_recyclability.list[index].labels.concat(material.labels)
          } else {
            this.productsToUpdate[entry.sku].sustainability.spanish_recyclability.list.push(material)
          }

          // material.index = entry.numbering
          // material
          // if (entry.custom_name) {
          //   material.key = entry.custom_name
          // } else {
          //   material.key = entry.material_key
          // }
          // material.material = entry.material_translation_key
          // material.packaging = entry.packaging_key
          // material.collectionGuideline = entry.collection_key
        }
      })
      removalSkus.forEach((sku) => {
        if (!Object.prototype.hasOwnProperty.call(this.productsToUpdate, sku)) {
          this.productsToUpdate[sku] = {}
        }
        this.productsToUpdate[sku].sustainability = this.productsToUpdate[sku].sustainability ||
          { organic_icons: [], recyclability_icons: [], certifications_icons: [], custom_icons: [] }
        this.productsToUpdate[sku].sustainability.spanish_recyclability =
          this.productsToUpdate[sku].sustainability.spanish_recyclability || {}
        this.productsToUpdate[sku].sustainability.spanish_recyclability.list = CLEAR_LIST_FLAG
        this.recordRemoval(sku, 'Spanish recycling (all entries)')
      })
    },

    processSettingsSheet () {
      this.settingsSheet.forEach(entry => {
        if (!entry.sku) return
        if (!Object.prototype.hasOwnProperty.call(this.productsToUpdate, entry.sku)) {
          this.productsToUpdate[entry.sku] = {}
        }

        // color theme
        this.productsToUpdate[entry.sku].theme = this.productsToUpdate[entry.sku].theme || {}
        if (!this.applyFieldRemoval(entry, ['primary_color'], this.productsToUpdate[entry.sku].theme, 'primary', 'primary color')) {
          if (this.hasProvidedValue(entry.primary_color)) {
            this.productsToUpdate[entry.sku].theme.primary = entry.primary_color
          }
        }
        if (!this.applyFieldRemoval(entry, ['background_color'], this.productsToUpdate[entry.sku].theme, 'background', 'background color')) {
          if (this.hasProvidedValue(entry.background_color)) {
            this.productsToUpdate[entry.sku].theme.background = entry.background_color
          }
        }
        if (!this.applyFieldRemoval(entry, ['alcohol_color'], this.productsToUpdate[entry.sku].theme, 'alcoholColor', 'alcohol color')) {
          if (this.hasProvidedValue(entry.alcohol_color)) {
            this.productsToUpdate[entry.sku].theme.alcoholColor = entry.alcohol_color
          }
        }

        // header
        this.productsToUpdate[entry.sku].header = this.productsToUpdate[entry.sku].header || {}
        if (!this.applyFieldRemoval(entry, ['page_title'], this.productsToUpdate[entry.sku].header, 'title', 'page title')) {
          if (this.hasProvidedValue(entry.page_title)) {
            this.productsToUpdate[entry.sku].header.title = entry.page_title
          }
        }
        if (isRemoveKeyword(entry.logo_key)) {
          this.productsToUpdate[entry.sku].header.logo = REMOVE_FIELD_FLAG
          this.recordRemoval(entry.sku, 'page logo')
        } else if (entry.logo_key) {
          const matchingImage = this.gallery.find((image) => image.key === entry.logo_key.trim())
          if (matchingImage) {
            this.productsToUpdate[entry.sku].header.logo = matchingImage.file
          }
        }

        // footer
        this.productsToUpdate[entry.sku].footer = this.productsToUpdate[entry.sku].footer || {}
        if (this.applyFieldRemoval(entry, ['privacy_link'], this.productsToUpdate[entry.sku].footer, 'privacy', 'privacy link')) {
          // the privacy text is the URL's label and never renders without it - remove them together
          this.productsToUpdate[entry.sku].footer.privacy_description = CLEAR_OBJECT_FLAG
        } else if (this.hasProvidedValue(entry.privacy_link)) {
          this.productsToUpdate[entry.sku].footer.privacy = entry.privacy_link
        }
        if (!this.applyFieldRemoval(entry, ['page_footer_line_1'], this.productsToUpdate[entry.sku].footer, 'first_line', 'footer line 1')) {
          if (this.hasProvidedValue(entry.page_footer_line_1)) {
            this.productsToUpdate[entry.sku].footer.first_line = entry.page_footer_line_1
          }
        }
        if (!this.applyFieldRemoval(entry, ['page_footer_line_2'], this.productsToUpdate[entry.sku].footer, 'second_line', 'footer line 2')) {
          if (this.hasProvidedValue(entry.page_footer_line_2)) {
            this.productsToUpdate[entry.sku].footer.second_line = entry.page_footer_line_2
          }
        }

        if (!this.applyFieldRemoval(entry, ['default_country_key'], this.productsToUpdate[entry.sku], 'default_country', 'default country')) {
          if (entry.default_country_key && entry.default_country_key !== '#N/A') {
            this.productsToUpdate[entry.sku].default_country = entry.default_country_key
          }
        }

        if (entry.show_content_in_language_of_country !== undefined && entry.show_content_in_language_of_country !== null && entry.show_content_in_language_of_country !== '') {
          this.productsToUpdate[entry.sku].enforce_language_per_country = entry.show_content_in_language_of_country === true || (typeof entry.show_content_in_language_of_country === 'string' && entry.show_content_in_language_of_country.toLowerCase() === 'true') || false
        }
      })
    },

    convertToHTML (string) {
      const formattedStr = string
        .replaceAll('{', '<b>').replaceAll('}', '</b>')
        // .replaceAll('<<', '<i>').replaceAll('>>', '</i>')
        // .replaceAll('[[', '<u>').replaceAll(']]', '</u>')

      const splitLines = formattedStr.split(/\r?\n|\r|\\n|\u2028|\u2029/) // detects line breaks in Excel

      if (splitLines.length === 0) {
        return '<p></p>'
      } else if (splitLines.length === 1) {
        return `<p>${splitLines[0]}</p>`
      } else {
        return splitLines.map(line => `<p>${line}</p>`).join('')
      }
    },

    processPackagingInformationSheet () {
      const removalSkus = collectSkusToClear(this.packagingInformationSheet)
      const skusStartedThisUpload = new Set()
      this.packagingInformationSheet.forEach((entry) => {
        if (isRemoveAllRow(entry)) return
        const sku = entry.sku?.toString().trim()
        if (!sku) return
        const hasAnyValue = this.hasProvidedValue(entry.packaging_operator_type) || this.hasProvidedValue(entry.company_name_and_address) || this.hasProvidedValue(entry.contact)
        if (!hasAnyValue) return

        if (!Object.prototype.hasOwnProperty.call(this.productsToUpdate, sku)) {
          this.productsToUpdate[sku] = {}
        }
        this.productsToUpdate[sku].sustainability = this.productsToUpdate[sku].sustainability || { organic_icons: [], recyclability_icons: [], certifications_icons: [], custom_icons: [] }
        if (!skusStartedThisUpload.has(sku)) {
          this.productsToUpdate[sku].sustainability.packaging_information = []
          skusStartedThisUpload.add(sku)
        }

        const packagingOperatorTypeKey = entry.packaging_operator_type_key && entry.packaging_operator_type_key !== '#N/A' ? String(entry.packaging_operator_type_key) : ''
        // a blank operator type cell stays blank — the entry renders without a label line
        let label = ''
        if (packagingOperatorTypeKey) {
          label = packagingOperatorTypeKey
        } else if (this.hasProvidedValue(entry.packaging_operator_type)) {
          label = { [this.settings.defaultLanguage]: String(entry.packaging_operator_type) }
        }
        this.productsToUpdate[sku].sustainability.packaging_information.push({
          label,
          company: this.hasProvidedValue(entry.company_name_and_address) ? String(entry.company_name_and_address) : '',
          contact: this.hasProvidedValue(entry.contact) ? String(entry.contact) : ''
        })
      })
      removalSkus.forEach((sku) => {
        if (!Object.prototype.hasOwnProperty.call(this.productsToUpdate, sku)) {
          this.productsToUpdate[sku] = {}
        }
        this.productsToUpdate[sku].sustainability = this.productsToUpdate[sku].sustainability ||
          { organic_icons: [], recyclability_icons: [], certifications_icons: [], custom_icons: [] }
        this.productsToUpdate[sku].sustainability.packaging_information = CLEAR_LIST_FLAG
        this.recordRemoval(sku, 'packaging information (all entries)')
      })
    },

    processTranslatableFieldsSheet () {
      const freeTextFieldKeys = ['ingredients'] // fields that should be HTML-wrapped

      this.translatableFieldsSheet.forEach(entry => {
        const sku = entry.sku?.toString().trim()
        const fieldKey = entry.field?.toString().trim()
        if (!sku || !fieldKey) return

        if (!Object.prototype.hasOwnProperty.call(this.productsToUpdate, sku)) {
          this.productsToUpdate[sku] = {}
        }

        // Build language → text map (HTML only for freeTextFieldKeys)
        const translations = {}
        for (const columnHeader in entry) {
          if (columnHeader === 'sku' || columnHeader === 'field') continue

          const languageIndex = Languages.findIndex(lang => lang.description === columnHeader)
          if (languageIndex < 0) continue

          const cellValue = entry[columnHeader] ?? ''
          const langKey = Languages[languageIndex].key

          translations[langKey] = isRemoveKeyword(cellValue)
            ? REMOVE_FIELD_FLAG
            : freeTextFieldKeys.includes(fieldKey)
              ? this.convertToHTML(String(cellValue))
              : String(cellValue)

          if (isRemoveKeyword(cellValue)) {
            this.recordRemoval(sku, `${fieldKey} (${columnHeader})`)
          }
        }

        // Rows with no filled language cells must not touch the collected changes — the mere
        // presence of a row would otherwise cancel removal flags written from other sheets
        // (and crash on the list flags, which are strings)
        if (!Object.keys(translations).length) return

        if (fieldKey === 'ingredients') {
          this.productsToUpdate[sku].ingredients = this.productsToUpdate[sku].ingredients || {}
          this.productsToUpdate[sku].ingredients.free_text = translations
          // A removal-only row (every language cell is #REMOVE) clears the text without
          // claiming manual mode - otherwise pairing new structured Ingredients rows with a
          // dragged-down #REMOVE translatable row would flip the product into empty manual
          // mode and hide the fresh structured list.
          const hasIngredientContent = Object.keys(translations).some((langKey) => translations[langKey] !== REMOVE_FIELD_FLAG)
          if (hasIngredientContent) {
            this.productsToUpdate[sku].ingredients.use_free_form = true
          }
        }
        if (fieldKey === 'product_display_name') {
          this.productsToUpdate[sku]['product-infos'] = this.productsToUpdate[sku]['product-infos'] || {}
          this.productsToUpdate[sku]['product-infos'].product_name = translations
        }
        if (fieldKey === 'product_type') {
          this.productsToUpdate[sku]['product-infos'] = this.productsToUpdate[sku]['product-infos'] || {}
          this.productsToUpdate[sku]['product-infos'].type = translations
        }
        if (fieldKey === 'product_origin_expression') {
          this.productsToUpdate[sku]['product-infos'] = this.productsToUpdate[sku]['product-infos'] || {}
          // Only assign origin_prefix if default language has content; allows defaulting to 'Produced in:' if no string entered
          if (translations[this.settings.defaultLanguage] && translations[this.settings.defaultLanguage].trim()) {
            this.productsToUpdate[sku]['product-infos'].origin_prefix = translations
          }
        }
        if (fieldKey === 'ingredient_category' && !this.isType(['food_and_beverage'])) {
          // The default-language cell is the category name and the key of the entry,
          // so a row without it is skipped.
          const defaultLanguageCell = translations[this.settings.defaultLanguage]
          const categoryName = typeof defaultLanguageCell === 'string' && defaultLanguageCell !== REMOVE_FIELD_FLAG
            ? defaultLanguageCell.trim()
            : ''
          if (!categoryName) return
          translations[this.settings.defaultLanguage] = categoryName
          this.productsToUpdate[sku].ingredients = this.productsToUpdate[sku].ingredients || {}
          const categoryLabels = this.productsToUpdate[sku].ingredients.category_labels || {}
          categoryLabels[categoryName] = translations
          this.productsToUpdate[sku].ingredients.category_labels = categoryLabels
        }
        if (fieldKey === 'ingredient_name' && !this.isType(['food_and_beverage'])) {
          // Applied later on the merged label, because the ingredient rows may come from this
          // upload or from the stored label.
          const defaultLanguageCell = translations[this.settings.defaultLanguage]
          const ingredientName = typeof defaultLanguageCell === 'string' && defaultLanguageCell !== REMOVE_FIELD_FLAG
            ? defaultLanguageCell.trim()
            : ''
          if (!ingredientName) return
          this.pendingIngredientNameTranslations[sku] = this.pendingIngredientNameTranslations[sku] || {}
          this.pendingIngredientNameTranslations[sku][ingredientName] = translations
        }
        const packagingLabelMatch = fieldKey.match(/^packaging_operator_type_(\d+)$/)
        if (packagingLabelMatch && parseInt(packagingLabelMatch[1]) >= 1 && Object.keys(translations).length) {
          const entryIndex = parseInt(packagingLabelMatch[1]) - 1
          this.productsToUpdate[sku].sustainability = this.productsToUpdate[sku].sustainability || { organic_icons: [], recyclability_icons: [], certifications_icons: [], custom_icons: [] }
          // removal wins: a remove-all on the Packaging Information sheet beats label rows
          if (this.productsToUpdate[sku].sustainability.packaging_information === CLEAR_LIST_FLAG) return
          if (!Array.isArray(this.productsToUpdate[sku].sustainability.packaging_information)) {
            this.productsToUpdate[sku].sustainability.packaging_information = []
          }
          const packagingEntries = this.productsToUpdate[sku].sustainability.packaging_information
          while (packagingEntries.length <= entryIndex) {
            packagingEntries.push({ label: '', company: '', contact: '' })
          }
          packagingEntries[entryIndex].label = translations
        }
        if (fieldKey === 'product_description') {
          this.productsToUpdate[sku]['product-infos'] = this.productsToUpdate[sku]['product-infos'] || {}
          this.productsToUpdate[sku]['product-infos'].sales_description = translations
        }
        if (fieldKey === 'production_method' && this.isType(['wine'])) {
          this.productsToUpdate[sku]['product-infos'] = this.productsToUpdate[sku]['product-infos'] || {}
          this.productsToUpdate[sku]['product-infos'].production_method = translations
        }
        if (fieldKey === 'ageing/production_methods_(spirit)' && this.isType(['spirit'])) {
          this.productsToUpdate[sku]['product-infos'] = this.productsToUpdate[sku]['product-infos'] || {}
          this.productsToUpdate[sku]['product-infos'].ageing_methods = translations
        }
        if (fieldKey === 'wine_colour' && this.isType(['wine'])) {
          this.productsToUpdate[sku]['product-infos'] = this.productsToUpdate[sku]['product-infos'] || {}
          this.productsToUpdate[sku]['product-infos'].wine_colour = translations
        }
        if (fieldKey === 'traditional_terms' && this.isType(['wine'])) {
          this.productsToUpdate[sku]['product-infos'] = this.productsToUpdate[sku]['product-infos'] || {}
          this.productsToUpdate[sku]['product-infos'].traditional_terms = translations
        }
        if (fieldKey === 'privacy_link_text') {
          this.productsToUpdate[sku].footer = this.productsToUpdate[sku].footer || {}
          this.productsToUpdate[sku].footer.privacy_description = translations
        }
        if (fieldKey === 'responsible_consumption_message_(spirit/other)' && this.isType(['spirit', 'food_and_beverage'])) {
          this.productsToUpdate[sku]['responsible-consumption'] = this.productsToUpdate[sku]['responsible-consumption'] || {}
          this.productsToUpdate[sku]['responsible-consumption'].description = translations
        }
        if (fieldKey === 'recyclability_message') {
          this.productsToUpdate[sku].sustainability = this.productsToUpdate[sku].sustainability || {}
          this.productsToUpdate[sku].sustainability.recyclability_description = translations
        }
        if (fieldKey === 'organic_message') {
          this.productsToUpdate[sku].sustainability = this.productsToUpdate[sku].sustainability || {}
          this.productsToUpdate[sku].sustainability.organic_description = translations
        }
        if (fieldKey === 'certifications_message') {
          this.productsToUpdate[sku].sustainability = this.productsToUpdate[sku].sustainability || {}
          this.productsToUpdate[sku].sustainability.certifications_description = translations
        }
        if (fieldKey.startsWith('additional_information_link_text')) {
          this.productsToUpdate[sku]['brand-infos'] = this.productsToUpdate[sku]['brand-infos'] || {}
          const brandInfo = this.productsToUpdate[sku]['brand-infos']
          // removal wins: link text without its removed URL is meaningless
          if (brandInfo.links && !Array.isArray(brandInfo.links)) return
          brandInfo.links = brandInfo.links || []

          // e.g. maps "additional_information_link_text_1" to links[0].text
          // the fallback handles legacy format when it was "additional_information_link_1_text"
          const linkNumber = parseInt(fieldKey.split('_').pop()) ? parseInt(fieldKey.split('_').pop()) : parseInt(fieldKey.split('_')[3])
          const linkIndex = linkNumber - 1
          if (linkIndex >= 0) {
            while (brandInfo.links.length <= linkIndex) {
              brandInfo.links.push({ link: '', text: {} })
            }
            brandInfo.links[linkIndex].text = translations
          }
        }
        if (fieldKey.startsWith('product_custom_field')) {
          this.productsToUpdate[sku]['product-infos'] = this.productsToUpdate[sku]['product-infos'] || {}
          this.productsToUpdate[sku]['product-infos'].custom_fields = this.productsToUpdate[sku]['product-infos'].custom_fields || []

          // e.g. maps "product_custom_field_label_1" to custom_fields[0].label
          const fieldParts = fieldKey.split('_')
          const fieldNumber = parseInt(fieldParts.pop())
          const fieldType = fieldParts.pop()
          const fieldIndex = fieldNumber - 1

          if (fieldIndex >= 0) {
            while (this.productsToUpdate[sku]['product-infos'].custom_fields.length <= fieldIndex) {
              this.productsToUpdate[sku]['product-infos'].custom_fields.push({ label: {}, value: {} })
            }
            this.productsToUpdate[sku]['product-infos'].custom_fields[fieldIndex][fieldType] = translations
          }
        }
        if (fieldKey.startsWith('free-section')) {
          this.productsToUpdate[sku]['free-sections'] = this.productsToUpdate[sku]['free-sections'] || { sections: [] }

          // e.g. maps "free-section_title_1" to free-sections.sections[0].title
          const fieldParts = fieldKey.split('_')
          const fieldNumber = parseInt(fieldParts.pop())
          const fieldType = fieldParts.pop()
          const fieldIndex = fieldNumber - 1

          if (fieldIndex >= 0) {
            while (this.productsToUpdate[sku]['free-sections'].sections.length <= fieldIndex) {
              this.productsToUpdate[sku]['free-sections'].sections.push({ title: {}, custom_icons: [], description: {} })
            }
            const targetProperty = fieldType === 'text' ? 'description' : 'title'
            this.productsToUpdate[sku]['free-sections'].sections[fieldIndex][targetProperty] = translations
          }
        }
      })
    },

    processCountrySpecificSheet () {
      this.countrySpecificSheet.forEach((entry) => {
        if (entry.sku) {
          if (!Object.prototype.hasOwnProperty.call(this.productsToUpdate, entry.sku)) {
            this.productsToUpdate[entry.sku] = {}
          }

          const freeText = {}
          for (const key in entry) {
            if (key !== 'sku' && key !== 'field' && key !== 'internal_field_key') {
              const index = Countries.findIndex((country) => {
                return country.description === key
              })
              if (~index) {
                freeText[Countries[index].key] = isRemoveKeyword(entry[key])
                  ? REMOVE_FIELD_FLAG
                  : entry.internal_field_key === 'impressum' ? this.convertToHTML(entry[key]) : entry[key]
                if (isRemoveKeyword(entry[key])) {
                  this.recordRemoval(entry.sku, `${entry.internal_field_key} (${key})`)
                }
              } else if (key === 'All countries') {
                freeText.default = isRemoveKeyword(entry[key])
                  ? REMOVE_FIELD_FLAG
                  : entry.internal_field_key === 'impressum' ? this.convertToHTML(entry[key]) : entry[key]
                if (isRemoveKeyword(entry[key])) {
                  this.recordRemoval(entry.sku, `${entry.internal_field_key} (all countries)`)
                }
              }
            }
          }
          if (entry.internal_field_key && entry.internal_field_key !== '#N/A' && entry.internal_field_key.indexOf(':') > -1) {
            const section = entry.internal_field_key.split(':')[0]
            const fieldName = entry.internal_field_key.split(':')[1]
            if (!this.productsToUpdate[entry.sku][section]) {
              this.productsToUpdate[entry.sku][section] = {}
            }
            if (section === 'business-operator' && fieldName.endsWith('_contact')) {
              const operatorKey = fieldName.slice(0, -'_contact'.length)
              if (!this.productsToUpdate[entry.sku][section][operatorKey]) {
                this.productsToUpdate[entry.sku][section][operatorKey] = {}
              }
              for (const entryKey in freeText) {
                this.productsToUpdate[entry.sku][section][operatorKey][`${entryKey}_contact`] = freeText[entryKey]
              }
            } else if (section === 'business-operator') {
              this.productsToUpdate[entry.sku][section][fieldName] = Object.assign(this.productsToUpdate[entry.sku][section][fieldName] || {}, freeText)
            } else {
              this.productsToUpdate[entry.sku][section][fieldName] = freeText
            }
          } else if (entry.internal_field_key === 'impressum') {
            this.productsToUpdate[entry.sku].impressum = { free_text: freeText }
          }
        }
      })
    },

    addDefaultTemplateInfos () {
      let defaultTemplate
      if (this.settings.selectedTemplate === 'copy') {
        defaultTemplate = JSON.parse(JSON.stringify(this.settings.templateElabel.data.published))
      } else {
        defaultTemplate = JSON.parse(JSON.stringify(this.settings.selectedTemplate.data))
      }

      // We already know which SKUs have an existing e-label thanks to parseFile() → elabelToLoad
      const existingSkuSet = new Set(this.elabelToLoad.map(elabel => elabel.product.sku))

      // Helper: decide whether the spreadsheet actually provided a value for a key
      const shouldFillWithDefault = (val) => {
        if (typeof val === 'string') return val.trim().length > 0 // blanks mean user didn't provide value
        if (typeof val === 'number' || typeof val === 'boolean') return true //  0 and false are counted as provided values
        if (Array.isArray(val)) return false // never ovewrite arrays with template for existing SKUs
        if (val && typeof val === 'object') return Object.keys(val).length > 0
        return false
      }

      // Helper: prevent skus from having shared object references
      const clone = (v) => (v && typeof v === 'object' ? JSON.parse(JSON.stringify(v)) : v)

      for (const sku in this.productsToUpdate) {
        const isExisting = existingSkuSet.has(sku)

        if (!isExisting) {
          const tmp = JSON.parse(JSON.stringify(defaultTemplate))
          this.productsToUpdate[sku] = Utils.mergeDeep(tmp, this.productsToUpdate[sku])
          continue
        }

        // For existing SKUs, preserve existing values while adding template defaults only where needed
        const productToUpdate = this.productsToUpdate[sku]
        const tmp = {}

        for (const topKey of Object.keys(productToUpdate)) {
          const templateTopLevel = defaultTemplate[topKey]
          if (templateTopLevel === undefined) continue

          // If value is an object (not an array), check its subkeys individually
          if (productToUpdate[topKey] && typeof productToUpdate[topKey] === 'object' && !Array.isArray(productToUpdate[topKey])) {
            const subkeys = Object.keys(productToUpdate[topKey])
            if (!subkeys.length) continue
            const nested = {}

            for (const subKey of subkeys) {
              const productToUpdateVal = productToUpdate[topKey][subKey]
              const templateSub = templateTopLevel?.[subKey]
              if (templateSub === undefined) continue
              if (shouldFillWithDefault(productToUpdateVal)) {
                nested[subKey] = clone(templateSub)
              }
            }

            if (Object.keys(nested).length) {
              tmp[topKey] = nested
            }
          } else {
            // For primitives/arrays at top level, only copy template defaults to tmp if spreadsheet provided a value
            if (shouldFillWithDefault(productToUpdate[topKey])) {
              tmp[topKey] = clone(templateTopLevel)
            }
          }
        }

        this.productsToUpdate[sku] = Utils.mergeDeep(tmp, productToUpdate)
      }
    },

    processTutorialSheet () {
      if (this.tutorialSheet && this.tutorialSheet[0]) {
        if (this.tutorialSheet[0].version) {
          this.uploadedTemplateVersion = this.tutorialSheet[0].version
        }
        if (this.tutorialSheet[0].Version) {
          this.uploadedTemplateVersion = this.tutorialSheet[0].Version
        }
        if (this.uploadedTemplateVersion !== this.latestTemplateVersion) {
          this.isOutdatedTemplate = true
        } else {
          this.isOutdatedTemplate = false
        }
      }
    },

    readFile (file) {
      const unprocessableFileMessage = 'This file could not be processed. Please check that it is a valid e-label template file and try again.'
      this.productsToUpdate = {}
      this.removals = {}
      this.pendingIngredientNameTranslations = {}
      const reader = new FileReader()
      reader.readAsArrayBuffer(file)
      reader.onload = () => {
        try {
          const data = new Uint8Array(reader.result)
          const workbook = read(data, { type: 'array' })
          const recognizedSheetNames = ['TutorialHelp', 'Product Information', 'Ingredients', 'Nutrition', 'Additional Information', 'Pictograms', 'Italian Recycling', 'Translatable Fields', 'Free Text Fields', 'Country-specific Fields', 'Spanish Recycling', 'Packaging Information', 'Settings']
          const hasRecognizedSheet = workbook.SheetNames.some((sheetName) => recognizedSheetNames.includes(sheetName))
          if (!hasRecognizedSheet) {
            // a wrong file (e.g. CSV bytes renamed .xlsx) parses without throwing - reject it up front
            this.$store.dispatch('showSnackBar', { message: unprocessableFileMessage })
            return
          }
          if (workbook.SheetNames.indexOf('TutorialHelp') > -1) {
            const sheet = workbook.Sheets[workbook.SheetNames[workbook.SheetNames.indexOf('TutorialHelp')]]
            this.tutorialSheet = this.cleanCellValues(utils.sheet_to_json(sheet))
          }
          if (workbook.SheetNames.indexOf('Product Information') > -1) {
            const sheet = workbook.Sheets[workbook.SheetNames[workbook.SheetNames.indexOf('Product Information')]]
            this.productInfoSheet = this.cleanCellValues(utils.sheet_to_json(sheet))
          }
          if (workbook.SheetNames.indexOf('Ingredients') > -1) {
            const sheet = workbook.Sheets[workbook.SheetNames[workbook.SheetNames.indexOf('Ingredients')]]
            this.ingredientsSheet = this.cleanCellValues(utils.sheet_to_json(sheet))
          }
          if (workbook.SheetNames.indexOf('Nutrition') > -1) {
            const sheet = workbook.Sheets[workbook.SheetNames[workbook.SheetNames.indexOf('Nutrition')]]
            this.nutritionSheet = this.cleanCellValues(utils.sheet_to_json(sheet))
          }
          if (workbook.SheetNames.indexOf('Additional Information') > -1) {
            const sheet = workbook.Sheets[workbook.SheetNames[workbook.SheetNames.indexOf('Additional Information')]]
            this.brandInformationSheet = this.cleanCellValues(utils.sheet_to_json(sheet))
          }
          if (workbook.SheetNames.indexOf('Pictograms') > -1) {
            const sheet = workbook.Sheets[workbook.SheetNames[workbook.SheetNames.indexOf('Pictograms')]]
            this.pictogramsSheet = this.cleanCellValues(utils.sheet_to_json(sheet))
          }
          if (workbook.SheetNames.indexOf('Italian Recycling') > -1) {
            const sheet = workbook.Sheets[workbook.SheetNames[workbook.SheetNames.indexOf('Italian Recycling')]]
            this.italianRecyclingSheet = this.cleanCellValues(utils.sheet_to_json(sheet))
          }
          if (workbook.SheetNames.indexOf('Translatable Fields') > -1 || workbook.SheetNames.indexOf('Free Text Fields') > -1) {
            const sheet = workbook.Sheets[workbook.SheetNames[workbook.SheetNames.indexOf('Translatable Fields')]] || workbook.Sheets[workbook.SheetNames[workbook.SheetNames.indexOf('Free Text Fields')]]
            this.translatableFieldsSheet = this.cleanCellValues(utils.sheet_to_json(sheet))
          }
          if (workbook.SheetNames.indexOf('Country-specific Fields') > -1) {
            const sheet = workbook.Sheets[workbook.SheetNames[workbook.SheetNames.indexOf('Country-specific Fields')]]
            this.countrySpecificSheet = this.cleanCellValues(utils.sheet_to_json(sheet))
          }
          if (workbook.SheetNames.indexOf('Spanish Recycling') > -1) {
            const sheet = workbook.Sheets[workbook.SheetNames[workbook.SheetNames.indexOf('Spanish Recycling')]]
            this.spanishRecyclingSheet = this.cleanCellValues(utils.sheet_to_json(sheet))
          }
          if (workbook.SheetNames.indexOf('Packaging Information') > -1) {
            const sheet = workbook.Sheets[workbook.SheetNames[workbook.SheetNames.indexOf('Packaging Information')]]
            this.packagingInformationSheet = this.cleanCellValues(utils.sheet_to_json(sheet))
          }
          if (workbook.SheetNames.indexOf('Settings') > -1) {
            const sheet = workbook.Sheets[workbook.SheetNames[workbook.SheetNames.indexOf('Settings')]]
            this.settingsSheet = this.cleanCellValues(utils.sheet_to_json(sheet))
          }

          this.processTutorialSheet()
          this.processProductSheet()
          this.processNutritionSheet()
          this.processIngredientsSheet()
          this.processBrandInformationSheet()
          this.processCountrySpecificSheet()
          this.processItalianRecyclingSheet()
          this.processSpanishRecyclingSheet()
          this.processPackagingInformationSheet()
          this.processSettingsSheet()
          this.processPictogramsSheet()
          this.processTranslatableFieldsSheet() // keep this right after all process[X]Sheet methods

          // console.log('Before addDefaultTemplateInfos:', JSON.parse(JSON.stringify(this.productsToUpdate)))
          this.parseFile()
          this.addDefaultTemplateInfos()
          // console.log('Final productsToUpdate:', JSON.parse(JSON.stringify(this.productsToUpdate)))
        } catch (error) {
          // xlsx.read() on a corrupt file, or any processor bug, must not fail silently and
          // strand the user on a Next button that does nothing - surface it and stay on step 1
          console.error('[bulk-upload] failed to parse the uploaded file', error)
          this.step = 1
          this.$store.dispatch('showSnackBar', { message: unprocessableFileMessage })
        }
      }
    },

    onFileChange ($event) {
      if ($event.target.files[0]) {
        this.file = $event.target.files[0]
      }
    },

    onSearchProductBlur () {
      if (this.settings.templateProduct === null || (typeof this.settings.templateProduct === 'object' && !Object.keys(this.settings.templateProduct).length)) {
        this.searchProduct = ''
      }
    }
  },
  async created () {
    this.debouncedSearch = Utils.debounce((str) => {
      this.isLoadingProduct = true
      this.$store.dispatch('loadProducts', {
        search: str
      }).finally(() => {
        this.isLoadingProduct = false
      })
    }, 400, false)

    this.$store.dispatch('loadGallery')
    await this.$store.dispatch('loadFullProductList', {})
    this.fullProductListLoaded = true
  }
}

</script>

<style lang="sass" scoped>

.bulk-upload
  height: calc(100vh - 64px)
  overflow: scroll
  width: 100%

.delimiter
  height: 1px
  width: 100%
  background: #DCDCDC

.cut-margin
  margin-right: 0px

.comma
  padding-right: 3px

.bulk-row
  width: 90%
  max-width: 1500px
  margin-left: auto
  margin-right: auto
  .v-stepper
    width: 100%

.bulk-upload-ctn
  margin: 20px

.overview-title
  min-width: 150px
  font-size: 18px

.file-summary-card
  .number
    font-size: 20px
  &.new-elabel
    .number
      color: #15BBF0
    background: rgba(21, 187, 240, 0.2)
  &.update-elabel
    .number
      color: #3FC135
    background: rgba(63, 193, 53, 0.2)
  &.not-found
    .number
      color: #F22A42
    background: rgba(242, 42, 66, 0.2)

.upload-btn
  text-align: center
  width: 100%
  height: 100%
  background: #f1f1f1
  padding: 15px
  cursor: pointer
  .v-icon
    width: 35px

  .format-hint
    word-break: break-all
    margin-top: 15px

  .text-uppercase
    color: #00aeef
    font-size: 14px

.hint
  color: #5D5D5D

.custom-stepper
  padding: 0px
  .footer
    margin: 0px 24px 16px 24px
  .content
    padding: 24px 16px
  .loading
    .progress-ctn
      width: 80%
      margin-left: auto
      margin-right: auto
      margin-top: 20px
      .v-progress-linear
        height: 6px !important
    .infos
      padding: 50px 0px
      color: black
      font-size: 20px
      opacity: 1
      .upload-recap
        font-size: 14px
        text-align: center

.error-ctn
  text-align: center
  max-width: 60%
  margin-left: auto
  margin-right: auto

.instructions-ctn
  background-color: #F5F5F5
  li
    margin-bottom: 6px
  ul ul
    list-style-type: disc
    margin-top: 6px
  .instructions-title
    font-size: 14px
    color: #15BBF0
    text-transform: uppercase
    margin-bottom: 8px

.outdated-template-ctn
  color: #FF7F00 !important
  background-color: #ffe5cc
  padding: 5px 10px
  margin-left: 15px

</style>
